import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import * as Sentry from "@sentry/react";
import { loopLog, useValueChurnProbe } from "../utils/loopDebug";
import { API_CONFIG, getApiUrl } from "../config/api";
import { getClientSessionId } from "../utils/clientSession";
import { actingLocationHeaders } from "../utils/actingLocation";
import {
  REQUEST_ID_HEADER,
  createRequestId,
  recordFailedRequest,
} from "../utils/requestId";
import {
  isEmptyObject,
  recordApiEmptyResponseWarning,
} from "../utils/apiEmptyResponseWarning";
import { parseContentDispositionFilename } from "../utils/contentDispositionFilename";
import { salonDocumentUploadFilename } from "../utils/salonDocumentUpload";
import { getCurrentUser, fetchAuthSession, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { createRefreshDeduper } from "../utils/refreshDedupe";
import { isTransientError } from "../utils/authErrors";
import { getJwtExpiryMs, isJwtDecodeError } from "../utils/jwt";
import {
  EditTransactionRequest,
  EditTransactionResponse,
  EditTransactionLineItemsRequest,
  EditTransactionLineItemsResponse,
  VoidTransactionRequest,
  VoidTransactionPreviewResponse,
  VoidTransactionResponse,
} from "../types/transactions";
import {
  ProcessNonCardPaymentRequest,
  ProcessNonCardPaymentResponse,
  RefundPaymentRequest,
  RefundPaymentResponse,
  SendReceiptRequest,
  SendReceiptResponse,
} from "../types/payments";
import {
  ImageUploadRequest,
  ImageUploadResponse,
  GetImagesRequest,
  GetImagesResponse,
  DeleteImageRequest,
  DeleteImageResponse,
} from "../types/staticWebGenerator";
import {
  CreateStaffHoursOverrideBody,
  EffectiveStaffHours,
  GetStaffHoursOverridesParams,
  StaffHoursOverride,
} from "../types/scheduleAdjustment";
import {
  DocumentListResponse,
  DocumentUploadResponse,
  DocumentDetailResponse,
  DocumentRevertResponse,
  DocumentDeleteResponse,
  SalonDocumentPhidDownload,
} from "../types/salonDocument";
import {
  GetPayrollPeriodRequest,
  GetPayrollPeriodResponse,
  CreateBusinessRequest,
  CreateBusinessResponse,
  GetActiveUsersRequest,
  GetActiveUsersResponse,
  SyncPayrollRequest,
  SyncPayrollResponse,
  InitiateSsoResponse,
  PrepPayrollSyncRequest,
  PrepPayrollSyncResponse,
} from "../types/rollfi";
import {
  SubscriptionTierType,
  SubscriptionTierPricing,
} from "../types/subscription";
import {
  OnboardingProgressResponse,
  UpdateOnboardingProgressRequest,
  GetOnboardingQuestionsResponse,
  UpsertOnboardingAnswerRequest,
  UpsertOnboardingAnswerResponse,
} from "../components/onboarding/types/onboarding";
import {
  CreateQuickSaleRequest,
  CreateQuickSaleResponse,
} from "../types/quickSale";
import {
  ProductStockAdjustment,
  CreateProductStockAdjustmentRequest,
  UpdateProductStockAdjustmentRequest,
} from "../types/productQuantityAdjustments";
import {
  TimeTrackingStatusResponse,
  TimeTrackingStatusRawResponse,
  TimeTrackingActionRequest,
  TimeTrackingActionResponse,
  GetPayPeriodTimeCardsResponse,
  GetEmployeePayPeriodTimeCardResponse,
  UpdateTimeCardApprovalsResponse,
  DeleteTimeEntryResponse,
  GetTimeTrackingPolicyResponse,
  AddTimeTrackingPolicyResponse,
  UpdateTimeTrackingPolicyResponse,
  DeleteTimeTrackingPolicyResponse,
  PolicyAssignmentsResponse,
  UpsertTimeEntryData,
  UpsertTimeEntryResponse,
  SyncTimeCardsToPayrollResponse,
} from "../types/timeTracking";
import {
  AvailabilityRequest,
  AvailabilityResponse,
} from "../types/availability";
import type { NotificationFilters } from "../types/notifications";
import {
  Policy,
  CreatePolicyRequest,
  UpdatePolicyRequest,
  DefaultPolicy,
} from "../types/policies";
import {
  GetCommissionsResponse,
  CreateCommissionsRequest,
  CreateCommissionsResponse,
  DeleteCommissionResponse,
} from "../types/commission";
import {
  StaffTier,
  StaffTierCommissionDefault,
  CreateStaffTierRequest,
  UpdateStaffTierRequest,
  ReorderStaffTiersRequest,
  GetStaffTiersResponse,
  AssignStaffTierRequest,
  UpsertCommissionDefaultRequest,
  GetCommissionDefaultResponse,
  UpsertServiceDefaultsRequest,
  GetServiceDefaultsResponse,
  ApplyCommissionDefaultsRequest,
  ApplyServiceDefaultsRequest,
  ApplyDefaultsResponse,
  ReassignAndDeleteRequest,
  ReassignAndDeleteResponse,
} from "../types/staffTier";
import { ReorderStaffRequest } from "../types/salon";
import {
  SalonPaymentMethod,
  AddSalonPaymentMethodResponse,
  SalonPaymentMethodsListResponse,
} from "../types/salonPaymentMethods";
import {
  DiscountPreset,
  DiscountPresetsListResponse,
  DiscountPresetResponse,
  CreateDiscountPresetRequest,
  UpdateDiscountPresetRequest,
  DeleteDiscountPresetResponse,
  SetQuickDiscountsRequest,
} from "../types/discounts";
import {
  CreateSetupIntentRequest,
  CreateSetupIntentResponse,
  GetClientPaymentMethodsResponse,
  SetDefaultClientPaymentMethodResponse,
  SetClientPaymentMethodNicknameResponse,
  ChargeCardOnFileRequest,
  ChargeCardOnFileResponse,
  DeletePaymentMethodResponse,
} from "../types/cardOnFile";
import { FeatureAccessResponse } from "../types/featureAccess";
import {
  Role,
  GetRolePermissionsResponse,
  BatchUpdatePermissionsRequest,
  BatchUpdatePermissionsResponse,
  ResetPermissionsResponse,
} from "../types/workflowPermissions";
import {
  CashDrawerBalancingSummaryResponse,
  CashDrawerOverviewResponse,
  CashDrawerSingleResponse,
  CashTransaction,
  CountDrawerRequest,
  CountDrawerResponse,
  CreateCashTransactionRequest,
  UpdateBalancingDateResponse,
} from "../types/cashDrawer";
import { ClientActivityResponse } from "../types/clientActivity";
import {
  ServicePackage,
  ClientServicePackage,
  CreateServicePackageRequest,
  UpdateServicePackageRequest,
  ArchiveServicePackageResponse,
  SellServicePackageRequest,
  SellServicePackageResponse,
  RedeemPackageRequest,
  RedeemPackageResponse,
  ClientPackageAdjustment,
  AddPackageToClientRequest,
  AddPackageToClientResponse,
  AdjustBankRequest,
  AdjustBankResponse,
  EditExpiryRequest,
  EditExpiryResponse,
  RevokePackageRequest,
  RevokePackageResponse,
} from "../types/packages";
import {
  NotificationPreferencesResponse,
  UpdateNotificationPreferencesRequest,
  UpdateNotificationPreferencesResponse,
} from "../types/notificationPreferences";
import {
  GiftCardConfig,
  GiftCardsForClient,
  GiftCardListItem,
  ListGiftCardsResponse,
  GiftCardLedgerEntry,
  UpdateGiftCardConfigRequest,
  AddGiftCardRequest,
  AddGiftCardResponse,
  AdjustGiftCardRequest,
  AdjustGiftCardResponse,
  VoidGiftCardRequest,
  VoidGiftCardResponse,
  UpdateGiftCardCodeRequest,
  UpdateGiftCardCodeResponse,
  GiftCardSummary,
  SellGiftCardRequest,
  SellGiftCardResponse,
  TransactionGiftCardsResponse,
  LookupGiftCardByCodeResponse,
  AttachGiftCardRequest,
  AttachGiftCardResponse,
  SendGiftCardLinkRequest,
  SendGiftCardLinkResponse,
  RedeemGiftCardRequest,
  RedeemGiftCardResponse,
  GiftCardLineItemInput,
  CheckGiftCardCodeResponse,
} from "../types/giftCards";
import {
  LoyaltySettings,
  UpdateLoyaltySettingsRequest,
  LoyaltyEarnRule,
  CreateLoyaltyEarnRuleRequest,
  UpdateLoyaltyEarnRuleRequest,
  LoyaltyReward,
  CreateLoyaltyRewardRequest,
  UpdateLoyaltyRewardRequest,
  LoyaltyLocationSummaryResponse,
  ClientLoyaltyResponse,
  AdjustClientLoyaltyPointsRequest,
  SetClientLoyaltyOptInRequest,
  AvailableLoyaltyReward,
  RedeemLoyaltyRewardRequest,
  RedeemLoyaltyRewardResponse,
  LoyaltyEarnPreviewResponse,
} from "../types/loyalty";

interface AppContextType {
  getIdToken: () => Promise<string | null>;
  fetchWithAuth: (endpoint: string, options?: any) => Promise<any>;
  getSalons: () => Promise<any>;
  getSalonByUuid: (salonUuid: string) => Promise<any>;
  getServices: (salonId: string) => Promise<any>;
  createServices: (serviceData: any) => Promise<any>;
  updateServices: (serviceData: any) => Promise<any>;
  bulkUpdateBusinessCharges: (
    businessLocationId: string,
    payload: {
      services?: { id: string; default_business_charge: number }[];
      products?: { id: string; business_charge: number }[];
    },
  ) => Promise<any>;
  deleteSalonService: (salonServiceId: string) => Promise<any>;
  deleteStaffService: (staffServiceId: string) => Promise<any>;
  getSalonPolicies: (salonId: string) => Promise<Policy[]>;
  getPolicyDefaults: () => Promise<DefaultPolicy[]>;
  createPolicy: (policyData: CreatePolicyRequest) => Promise<Policy>;
  updatePolicy: (policyData: UpdatePolicyRequest) => Promise<Policy>;
  deletePolicy: (policyId: string) => Promise<any>;
  getTransactions: (salonId: string, queryParams?: string) => Promise<any>;
  getSales: (
    businessLocationId: string,
    view?: "transactions" | "purchases",
    queryParams?: string,
  ) => Promise<any>;
  getTransaction: (transactionId: string) => Promise<any>;
  getSuggestedTimes: (params: {
    services: string[];
    staffIds?: string[];
    startTime?: string;
    date: string;
    timezone: string;
  }) => Promise<any>;
  getClient: (clientId: string) => Promise<any>;
  addLineItemToTransaction: (
    transactionId: string,
    lineItemData: any,
  ) => Promise<any>;
  editTransaction: (
    transactionData: EditTransactionRequest,
  ) => Promise<EditTransactionResponse>;
  editTransactionLineItems: (
    transactionData: EditTransactionLineItemsRequest,
  ) => Promise<EditTransactionLineItemsResponse>;
  createAppointment: (appointmentData: any) => Promise<any>;
  updateAppointment: (appointmentData: any) => Promise<any>;
  createTimeBlock: (timeBlockData: any) => Promise<any>;
  updateTimeBlock: (timeBlockData: any) => Promise<any>;
  getTimeBlocks: (salonId: string, queryParams?: string) => Promise<any>;
  deleteTimeBlock: (timeBlockId: string, appliesTo: string) => Promise<any>;
  createSalon: (salonData: any) => Promise<any>;
  updateSalon: (salonData: any) => Promise<any>;
  getSalonStaff: (salonId: string) => Promise<any>;
  updateStaff: (staffData: any) => Promise<any>;
  deleteStaff: (staffId: string) => Promise<any>;
  inviteStaff: (inviteData: any) => Promise<any>;
  // Staff ad-hoc working-hours overrides ("Schedule Adjustments"). Core API,
  // Cognito-auth; live on test.
  createStaffHoursOverride: (
    body: CreateStaffHoursOverrideBody,
  ) => Promise<StaffHoursOverride>;
  deleteStaffHoursOverride: (overrideId: string) => Promise<any>;
  getStaffHoursOverrides: (
    staffId: string,
    params?: GetStaffHoursOverridesParams,
  ) => Promise<StaffHoursOverride[]>;
  getStaffHoursOverrideEffective: (
    staffId: string,
    date: string,
    businessLocationUuid?: string,
  ) => Promise<EffectiveStaffHours>;
  getSalonHoursOverrides: (
    businessLocationUuid: string,
    startDate: string,
    endDate: string,
  ) => Promise<StaffHoursOverride[]>;
  getCheckoutUrl: (checkoutData: any) => Promise<any>;
  getSubscriptionPricing: (
    tier: SubscriptionTierType,
  ) => Promise<SubscriptionTierPricing>;
  createStripeConnectAccount: (
    businessInfo: any,
    currentUser: any,
  ) => Promise<any>;
  getStripeConnectAccountLink: (accountId: string) => Promise<any>;
  initializeStripeConnectAccountSession: (sessionData: any) => Promise<any>;
  registerStripeConnectTerminalDevice: (
    deviceData: any,
    businessInfo: any,
  ) => Promise<any>;
  getStripeConnectTerminalDevice: (
    deviceId: string,
    accountId: string,
  ) => Promise<any>;
  removeStripeTerminalDevice: (
    deviceId: string,
    accountId: string,
    businessLocationId: string,
  ) => Promise<any>;
  getPaymentTerminals: (businessLocationId: string) => Promise<any>;
  updatePaymentTerminalLabel: (
    terminalId: string,
    businessLocationId: string,
    accountId: string,
    label: string,
  ) => Promise<any>;
  setTerminalConfiguration: (configData: any) => Promise<any>;
  createPaymentIntent: (paymentData: any) => Promise<any>;
  updatePaymentIntent: (paymentData: any) => Promise<any>;
  cancelPaymentIntent: (paymentIntentData: {
    paymentIntentId: string;
    terminalDeviceId: string;
    accountId: string;
  }) => Promise<any>;
  processPayment: (paymentData: any) => Promise<any>;
  simulatePayment: (paymentData: any) => Promise<any>;
  pollStripePaymentStatus: (
    transactionId: string,
    paymentIntentId: string,
  ) => Promise<any>;
  createQuickSale: (
    quickSaleData: CreateQuickSaleRequest,
  ) => Promise<CreateQuickSaleResponse>;
  getNotifications: (
    page?: number,
    size?: number,
    filters?: NotificationFilters,
  ) => Promise<any>;
  updateNotificationReadStatus: (notificationData: any) => Promise<any>;
  getNotificationPreferences: () => Promise<NotificationPreferencesResponse>;
  updateNotificationPreferences: (
    body: UpdateNotificationPreferencesRequest,
  ) => Promise<UpdateNotificationPreferencesResponse>;
  getDepositConfigs: (salonId: string) => Promise<any>;
  createDepositConfig: (depositData: any) => Promise<any>;
  updateDepositConfig: (depositData: any) => Promise<any>;
  deleteDepositConfig: (
    salonId: string,
    depositConfigId: string,
  ) => Promise<any>;
  getSalonPaymentMethods: (salonId: string) => Promise<SalonPaymentMethod[]>;
  addSalonPaymentMethod: (
    salonId: string,
    name: string,
  ) => Promise<AddSalonPaymentMethodResponse>;
  deleteSalonPaymentMethod: (
    salonId: string,
    paymentMethodId: string,
  ) => Promise<SalonPaymentMethodsListResponse>;
  setSalonPaymentMethodsSelection: (
    salonId: string,
    selectedIds: string[],
  ) => Promise<SalonPaymentMethodsListResponse>;
  getDiscountPresets: (salonId: string) => Promise<DiscountPreset[]>;
  createDiscountPreset: (
    salonId: string,
    body: CreateDiscountPresetRequest,
  ) => Promise<DiscountPreset>;
  updateDiscountPreset: (
    salonId: string,
    presetId: string,
    body: UpdateDiscountPresetRequest,
  ) => Promise<DiscountPreset>;
  deleteDiscountPreset: (
    salonId: string,
    presetId: string,
  ) => Promise<DeleteDiscountPresetResponse>;
  setQuickDiscounts: (
    businessLocationId: string,
    presetIds: string[],
  ) => Promise<DiscountPreset[]>;
  getTaxRates: (salonId: string) => Promise<any>;
  createTaxRate: (salonId: string, data: any) => Promise<any>;
  updateTaxRate: (taxRateId: string, data: any) => Promise<any>;
  deleteTaxRate: (
    taxRateId: string,
    options?: { force?: boolean },
  ) => Promise<any>;
  setProductTaxRates: (productId: string, taxRateIds: number[]) => Promise<any>;
  setSalonServiceTaxRates: (
    salonId: string,
    salonServiceId: string,
    taxRateIds: number[],
  ) => Promise<any>;
  processNonCardPayment: (
    nonCardPaymentData: ProcessNonCardPaymentRequest,
  ) => Promise<ProcessNonCardPaymentResponse>;
  getCashDrawerOverview: (
    salonId: string,
    options?: { ensureOpen?: boolean },
  ) => Promise<CashDrawerOverviewResponse>;
  getCashDrawerSession: (
    salonId: string,
    sessionId: string,
  ) => Promise<CashDrawerSingleResponse>;
  countCashDrawer: (
    sessionId: string,
    payload: CountDrawerRequest,
  ) => Promise<CountDrawerResponse>;
  recordCashAdjustment: (
    sessionId: string,
    payload: CreateCashTransactionRequest,
  ) => Promise<{
    transaction: CashTransaction;
  }>;
  voidCashTransaction: (transactionId: string) => Promise<{
    transaction: CashTransaction;
  }>;
  updateCashDrawerBalancingDate: (
    sessionId: string,
    balancingDate: string,
  ) => Promise<UpdateBalancingDateResponse>;
  getCashDrawerBalancingSummary: (
    sessionId: string,
  ) => Promise<CashDrawerBalancingSummaryResponse>;
  uploadImage: (request: ImageUploadRequest) => Promise<ImageUploadResponse>;
  removeSalonLogo: (
    businessLocationId: string,
  ) => Promise<{ message?: string }>;
  getImages: (request: GetImagesRequest) => Promise<GetImagesResponse>;
  deleteImage: (request: DeleteImageRequest) => Promise<DeleteImageResponse>;
  listSalonDocuments: (
    salonId: string,
    clientId?: string,
  ) => Promise<DocumentListResponse>;
  uploadSalonDocument: (
    salonId: string,
    documentKey: string,
    file: File | Blob,
    contentType: string,
    metadata?: Record<string, unknown>,
    clientId?: string,
    displayName?: string,
  ) => Promise<DocumentUploadResponse>;
  getSalonDocumentDetail: (
    salonId: string,
    documentKey: string,
    clientId?: string,
  ) => Promise<DocumentDetailResponse>;
  deleteSalonDocument: (
    salonId: string,
    documentKey: string,
    clientId?: string,
  ) => Promise<DocumentDeleteResponse | null>;
  revertSalonDocument: (
    salonId: string,
    documentKey: string,
    versionUuid: string,
    clientId?: string,
  ) => Promise<DocumentRevertResponse>;
  downloadSalonDocument: (
    salonId: string,
    documentKey: string,
    clientId: string,
  ) => Promise<SalonDocumentPhidDownload>;
  getRollfiPayrollPeriod: (
    companyId: string,
  ) => Promise<GetPayrollPeriodResponse>;
  createRollfiBusiness: (
    businessData: CreateBusinessRequest,
  ) => Promise<CreateBusinessResponse>;
  getRollfiActiveUsers: (
    request: GetActiveUsersRequest,
  ) => Promise<GetActiveUsersResponse>;
  syncRollfiPayroll: (
    payrollData: SyncPayrollRequest,
  ) => Promise<SyncPayrollResponse>;
  initiateSso: () => Promise<InitiateSsoResponse>;
  prepPayrollSync: (
    request: PrepPayrollSyncRequest,
  ) => Promise<PrepPayrollSyncResponse>;
  getTimeTrackingStatus: (
    userId: string,
    companyId: string,
  ) => Promise<TimeTrackingStatusResponse>;
  performTimeTrackingAction: (
    request: TimeTrackingActionRequest,
  ) => Promise<TimeTrackingActionResponse>;
  getPayPeriodTimeCards: (
    companyId: string,
    payPeriodId: string,
  ) => Promise<GetPayPeriodTimeCardsResponse>;
  getEmployeePayPeriodTimeCard: (
    userId: string,
    companyId: string,
    payPeriodId: string,
  ) => Promise<GetEmployeePayPeriodTimeCardResponse>;
  updateTimeCardApprovals: (
    companyId: string,
    payPeriodId: string,
    timeCardIds: string[],
    isApproved: boolean,
  ) => Promise<UpdateTimeCardApprovalsResponse>;
  deleteTimeEntry: (timeEntryId: string) => Promise<DeleteTimeEntryResponse>;
  upsertTimeEntry: (
    userId: string,
    companyId: string,
    timeEntry: UpsertTimeEntryData,
  ) => Promise<UpsertTimeEntryResponse>;
  syncTimeCardsToPayroll: (
    companyId: string,
    payPeriodId: string,
    timeCardIds: string[],
  ) => Promise<SyncTimeCardsToPayrollResponse>;
  getTimeTrackingPolicy: (
    companyId: string,
  ) => Promise<GetTimeTrackingPolicyResponse>;
  addTimeTrackingPolicy: (
    companyId: string,
    dailyOvertimeThreshold?: number,
    weeklyOvertimeThreshold?: number,
  ) => Promise<AddTimeTrackingPolicyResponse>;
  updateTimeTrackingPolicy: (
    companyId: string,
    timeTrackingPolicyId: string,
    dailyOvertimeThreshold?: number,
    weeklyOvertimeThreshold?: number,
  ) => Promise<UpdateTimeTrackingPolicyResponse>;
  deleteTimeTrackingPolicy: (
    companyId: string,
    timeTrackingPolicyId: string,
  ) => Promise<DeleteTimeTrackingPolicyResponse>;
  addPolicyAssignments: (
    companyId: string,
    timeTrackingPolicyId: string,
    userIds: string[],
  ) => Promise<PolicyAssignmentsResponse>;
  removePolicyAssignments: (
    companyId: string,
    timeTrackingPolicyId: string,
    userIds: string[],
  ) => Promise<PolicyAssignmentsResponse>;
  // Onboarding API methods
  getOnboardingProgress: (
    salonId?: string,
  ) => Promise<OnboardingProgressResponse | null>;
  updateOnboardingProgress: (
    data: UpdateOnboardingProgressRequest,
    salonId?: string,
  ) => Promise<OnboardingProgressResponse>;
  getOnboardingQuestions: (
    salonId?: string,
    questionKeys?: string[],
  ) => Promise<GetOnboardingQuestionsResponse>;
  upsertOnboardingAnswer: (
    data: UpsertOnboardingAnswerRequest,
    salonId?: string,
  ) => Promise<UpsertOnboardingAnswerResponse>;
  // Product stock adjustment methods
  createProductStockAdjustment: (
    productId: string,
    adjustmentData: CreateProductStockAdjustmentRequest,
  ) => Promise<ProductStockAdjustment>;
  updateProductStockAdjustment: (
    adjustmentId: string,
    updates: UpdateProductStockAdjustmentRequest,
  ) => Promise<ProductStockAdjustment>;
  deleteProductStockAdjustment: (adjustmentId: string) => Promise<void>;
  // Availability method for rebooking
  getAvailability: (
    params: AvailabilityRequest,
  ) => Promise<AvailabilityResponse>;
  // Commission methods
  getStaffCommissions: (staffId?: string) => Promise<GetCommissionsResponse>;
  createCommissions: (
    request: CreateCommissionsRequest,
  ) => Promise<CreateCommissionsResponse>;
  deleteCommission: (commissionId: string) => Promise<DeleteCommissionResponse>;
  // Staff Tier methods
  getStaffTiers: (salonId: string) => Promise<GetStaffTiersResponse>;
  createStaffTier: (
    salonId: string,
    request: CreateStaffTierRequest,
  ) => Promise<StaffTier>;
  updateStaffTier: (
    tierId: string,
    request: UpdateStaffTierRequest,
  ) => Promise<StaffTier>;
  reorderStaffTiers: (
    salonId: string,
    request: ReorderStaffTiersRequest,
  ) => Promise<GetStaffTiersResponse>;
  reorderStaff: (
    salonId: string,
    request: ReorderStaffRequest,
  ) => Promise<{ message: string }>;
  deleteStaffTier: (tierId: string) => Promise<{ message: string; id: string }>;
  assignStaffTier: (
    salonId: string,
    staffId: string,
    request: AssignStaffTierRequest,
  ) => Promise<{ staff_id: string; staff_tier_id: string | null }>;
  getTierCommissionDefault: (
    tierId: string,
  ) => Promise<GetCommissionDefaultResponse>;
  upsertTierCommissionDefault: (
    tierId: string,
    request: UpsertCommissionDefaultRequest,
  ) => Promise<StaffTierCommissionDefault>;
  deleteTierCommissionDefault: (tierId: string) => Promise<{ message: string }>;
  applyTierCommissionDefaults: (
    tierId: string,
    request: ApplyCommissionDefaultsRequest,
  ) => Promise<ApplyDefaultsResponse>;
  getTierServiceDefaults: (
    tierId: string,
  ) => Promise<GetServiceDefaultsResponse>;
  upsertTierServiceDefaults: (
    tierId: string,
    request: UpsertServiceDefaultsRequest,
  ) => Promise<GetServiceDefaultsResponse>;
  deleteTierServiceDefault: (
    tierId: string,
    salonServiceId: string,
  ) => Promise<{ message: string }>;
  applyTierServiceDefaults: (
    tierId: string,
    request: ApplyServiceDefaultsRequest,
  ) => Promise<ApplyDefaultsResponse>;
  reassignAndDeleteTier: (
    tierId: string,
    request: ReassignAndDeleteRequest,
  ) => Promise<ReassignAndDeleteResponse>;
  // Card-on-File methods
  createSetupIntent: (
    request: CreateSetupIntentRequest,
  ) => Promise<CreateSetupIntentResponse>;
  getClientPaymentMethods: (
    clientId: string,
  ) => Promise<GetClientPaymentMethodsResponse>;
  deleteClientPaymentMethod: (
    clientId: string,
    pmId: number,
  ) => Promise<DeletePaymentMethodResponse>;
  setDefaultClientPaymentMethod: (
    clientId: string,
    pmId: number,
  ) => Promise<SetDefaultClientPaymentMethodResponse>;
  setClientPaymentMethodNickname: (
    clientId: string,
    pmId: number,
    nickname: string,
  ) => Promise<SetClientPaymentMethodNicknameResponse>;
  chargeCardOnFile: (
    request: ChargeCardOnFileRequest,
  ) => Promise<ChargeCardOnFileResponse>;
  refundPayment: (
    request: RefundPaymentRequest,
  ) => Promise<RefundPaymentResponse>;
  sendReceipt: (
    paymentId: string,
    email?: string,
  ) => Promise<SendReceiptResponse>;
  // Service Packages (prepaid bundles)
  getServicePackages: (salonId: string) => Promise<ServicePackage[]>;
  createServicePackage: (
    salonId: string,
    body: CreateServicePackageRequest,
  ) => Promise<ServicePackage>;
  updateServicePackage: (
    salonId: string,
    packageId: string,
    body: UpdateServicePackageRequest,
  ) => Promise<ServicePackage>;
  deleteServicePackage: (
    salonId: string,
    packageId: string,
  ) => Promise<ArchiveServicePackageResponse>;
  getClientServicePackages: (
    salonId: string,
    clientId: string,
    includeInactive?: boolean,
  ) => Promise<ClientServicePackage[]>;
  getClientActivity: (
    salonId: string,
    clientId: string,
    cursor?: string | null,
    limit?: number,
  ) => Promise<ClientActivityResponse>;
  getClientPackageAdjustments: (
    salonId: string,
    cspId: string,
  ) => Promise<ClientPackageAdjustment[]>;
  // Manual package management (grant / adjust bank / edit expiry / revoke).
  // Each writes an immutable ClientPackageAdjustment audit row and requires a
  // reason category + note.
  addClientServicePackage: (
    salonId: string,
    clientId: string,
    body: AddPackageToClientRequest,
  ) => Promise<AddPackageToClientResponse>;
  adjustClientPackageBalance: (
    salonId: string,
    cspId: string,
    salonServiceId: string,
    body: AdjustBankRequest,
  ) => Promise<AdjustBankResponse>;
  editClientPackageExpiry: (
    salonId: string,
    cspId: string,
    body: EditExpiryRequest,
  ) => Promise<EditExpiryResponse>;
  revokeClientPackage: (
    salonId: string,
    cspId: string,
    body: RevokePackageRequest,
  ) => Promise<RevokePackageResponse>;
  sellServicePackage: (
    salonId: string,
    body: SellServicePackageRequest,
  ) => Promise<SellServicePackageResponse>;
  redeemPackage: (
    salonId: string,
    body: RedeemPackageRequest,
  ) => Promise<RedeemPackageResponse>;
  // Gift cards (prepaid stored-value balances) — salon config + client-owned
  // card management. Grant/adjust/void each write an immutable ledger entry and
  // require a reason category + note.
  getGiftCardConfig: (salonId: string) => Promise<GiftCardConfig>;
  updateGiftCardConfig: (
    salonId: string,
    body: UpdateGiftCardConfigRequest,
  ) => Promise<GiftCardConfig>;
  getClientGiftCards: (
    salonId: string,
    clientId: string,
  ) => Promise<GiftCardsForClient>;
  addClientGiftCard: (
    salonId: string,
    clientId: string,
    body: AddGiftCardRequest,
  ) => Promise<AddGiftCardResponse>;
  adjustGiftCardBalance: (
    salonId: string,
    giftCardId: string,
    body: AdjustGiftCardRequest,
  ) => Promise<AdjustGiftCardResponse>;
  voidGiftCard: (
    salonId: string,
    giftCardId: string,
    body: VoidGiftCardRequest,
  ) => Promise<VoidGiftCardResponse>;
  /**
   * Sales tab void. `{preview: true}` → verdict only (no mutation); `{}` →
   * performs the void; `{auto: true}` records it as an auto void (quick sale
   * exited unpaid). A blocked void rejects with the server's 409 message.
   */
  voidTransaction: {
    (
      businessLocationId: string,
      transactionId: string,
      body: { preview: true },
    ): Promise<VoidTransactionPreviewResponse>;
    (
      businessLocationId: string,
      transactionId: string,
      body?: VoidTransactionRequest,
    ): Promise<VoidTransactionResponse>;
  };
  updateGiftCardCode: (
    salonId: string,
    giftCardId: string,
    body: UpdateGiftCardCodeRequest,
  ) => Promise<UpdateGiftCardCodeResponse>;
  getGiftCardLedger: (
    salonId: string,
    giftCardId: string,
  ) => Promise<GiftCardLedgerEntry[]>;
  /** Salon-wide gift-card list for the Sales → Gift Cards tab (businessInfo.id). */
  listGiftCards: (businessLocationId: string) => Promise<GiftCardListItem[]>;
  // Phase 2 — staff-sold lifecycle. `businessLocationId` is the salon UUID
  // (businessInfo.id); these hit the core senary /business-locations routes.
  sellGiftCard: (
    businessLocationId: string,
    body: SellGiftCardRequest,
  ) => Promise<SellGiftCardResponse>;
  getTransactionGiftCards: (
    businessLocationId: string,
    transactionId: string,
  ) => Promise<GiftCardSummary[]>;
  lookupGiftCardByCode: (
    businessLocationId: string,
    code: string,
  ) => Promise<GiftCardSummary>;
  attachGiftCard: (
    businessLocationId: string,
    giftCardId: string,
    body: AttachGiftCardRequest,
  ) => Promise<AttachGiftCardResponse>;
  sendGiftCardLink: (
    businessLocationId: string,
    giftCardId: string,
    body: SendGiftCardLinkRequest,
  ) => Promise<SendGiftCardLinkResponse>;
  redeemGiftCard: (
    businessLocationId: string,
    body: RedeemGiftCardRequest,
  ) => Promise<RedeemGiftCardResponse>;
  // Phase 3 — is a staff-typed custom code free in this location? (backs the
  // debounced code field on the sell + add-line-item flows). Boolean only.
  checkGiftCardCodeAvailable: (
    businessLocationId: string,
    code: string,
  ) => Promise<boolean>;
  // Phase 3 — sell a gift card as a line item on a transaction. Wraps
  // addLineItemToTransaction with the `gift_cards[]` payload.
  addGiftCardLineItem: (
    transactionId: string,
    giftCard: GiftCardLineItemInput,
  ) => Promise<any>;
  // Feature access method
  getAccess: () => Promise<FeatureAccessResponse>;
  // Multi-location (PR A / #399): persist the viewing location for a
  // multi-location staff member (staff_users.last_used_salon_id).
  patchStaffUsersMe: (lastUsedSalonId: number) => Promise<any>;
  // Role permissions (RBAC) methods - workflow-based
  getRolePermissions: (salonId: string) => Promise<GetRolePermissionsResponse>;
  batchUpdateRolePermissions: (
    salonId: string,
    data: BatchUpdatePermissionsRequest,
  ) => Promise<BatchUpdatePermissionsResponse>;
  resetRolePermissions: (
    salonId: string,
    role: Role,
  ) => Promise<ResetPermissionsResponse>;
  // Loyalty (points-as-currency MVP) — backend PR2/PR5 not deployed yet;
  // callers must tolerate failures. `businessLocationId` = salon uuid
  // (businessInfo.id).
  getLoyaltySettings: (
    businessLocationId: string,
  ) => Promise<Partial<LoyaltySettings> | null>;
  getLoyaltyLocationSummary: (
    businessLocationId: string,
    params?: { start_date?: string; end_date?: string },
  ) => Promise<LoyaltyLocationSummaryResponse | null>;
  updateLoyaltySettings: (
    businessLocationId: string,
    data: UpdateLoyaltySettingsRequest,
  ) => Promise<LoyaltySettings>;
  getLoyaltyEarnRules: (
    businessLocationId: string,
  ) => Promise<LoyaltyEarnRule[]>;
  createLoyaltyEarnRule: (
    businessLocationId: string,
    data: CreateLoyaltyEarnRuleRequest,
  ) => Promise<LoyaltyEarnRule>;
  updateLoyaltyEarnRule: (
    businessLocationId: string,
    ruleId: string,
    data: UpdateLoyaltyEarnRuleRequest,
  ) => Promise<LoyaltyEarnRule>;
  deleteLoyaltyEarnRule: (
    businessLocationId: string,
    ruleId: string,
  ) => Promise<any>;
  getLoyaltyRewards: (businessLocationId: string) => Promise<LoyaltyReward[]>;
  createLoyaltyReward: (
    businessLocationId: string,
    data: CreateLoyaltyRewardRequest,
  ) => Promise<LoyaltyReward>;
  updateLoyaltyReward: (
    businessLocationId: string,
    rewardId: string,
    data: UpdateLoyaltyRewardRequest,
  ) => Promise<LoyaltyReward>;
  deleteLoyaltyReward: (
    businessLocationId: string,
    rewardId: string,
  ) => Promise<any>;
  // Client-loyalty endpoints take the NUMERIC business_locations.id (the
  // config endpoints above take the salon uuid).
  getClientLoyalty: (
    clientId: string,
    businessLocationId: number,
  ) => Promise<ClientLoyaltyResponse | null>;
  adjustClientLoyaltyPoints: (
    clientId: string,
    data: AdjustClientLoyaltyPointsRequest,
  ) => Promise<any>;
  setClientLoyaltyOptIn: (
    clientId: string,
    data: SetClientLoyaltyOptInRequest,
  ) => Promise<any>;
  getTransactionAvailableLoyaltyRewards: (
    transactionId: string,
  ) => Promise<AvailableLoyaltyReward[]>;
  redeemLoyaltyReward: (
    transactionId: string,
    data: RedeemLoyaltyRewardRequest,
  ) => Promise<RedeemLoyaltyRewardResponse>;
  getTransactionLoyaltyPreviewEarn: (
    transactionId: string,
    stagedRedemptionDollars?: number,
  ) => Promise<LoyaltyEarnPreviewResponse | null>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);

  const doRefreshToken = async (
    forceRefresh: boolean,
  ): Promise<string | null> => {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error("No ID token available");
      }

      // Base64url-safe decode — a bare atob() throws for some users' tokens.
      const expiryTime = getJwtExpiryMs(idToken);

      setAuthToken(idToken);
      setTokenExpiry(expiryTime);

      return idToken;
    } catch (error) {
      setTokenExpiry(null);
      // Transient network blips and the benign "no id token" throw above are
      // expected during normal operation (especially the mount refresh); only a
      // definitive auth error — a revoked/expired refresh token — is a real
      // "session is dying" signal. Gate the severity so these routine failures
      // don't drown the resilience dashboards in warning-level noise. A token
      // we can't decode is a bug, not a blip — keep it visible at warning.
      const errorType = isJwtDecodeError(error)
        ? "decode"
        : isTransientError(error)
          ? "transient"
          : "definitive";
      Sentry.captureMessage("auth.token_refresh_failed", {
        level: errorType === "transient" ? "info" : "warning",
        tags: {
          errorName: (error as { name?: string })?.name ?? "unknown",
          errorType,
          forceRefresh,
        },
      });
      return null;
    }
  };

  // Single-flight de-dupe of concurrent refreshes. Created once and always
  // calls the latest doRefreshToken via a ref. Crucially mode-aware: a forced
  // refresh never reuses an in-flight non-forced one (which could return the
  // stale cached token that just 401'd). See utils/refreshDedupe.ts.
  const doRefreshTokenRef = useRef(doRefreshToken);
  doRefreshTokenRef.current = doRefreshToken;
  const refreshTokenRef = useRef<
    ((forceRefresh?: boolean) => Promise<string | null>) | null
  >(null);
  if (!refreshTokenRef.current) {
    refreshTokenRef.current = createRefreshDeduper((forceRefresh) =>
      doRefreshTokenRef.current(!!forceRefresh),
    );
  }
  const refreshToken = (forceRefresh = false): Promise<string | null> =>
    refreshTokenRef.current!(forceRefresh);

  const getIdToken = async () => {
    const currentTime = Date.now();

    // If token doesn't exist or is expired/about to expire (within 5 minutes), refresh it
    if (!authToken || !tokenExpiry || currentTime >= tokenExpiry - 300000) {
      return refreshToken();
    }

    return authToken;
  };

  // Refresh token when component mounts
  useEffect(() => {
    refreshToken();
  }, []);

  // A backend 401 that survives a forced token refresh (or a refresh that
  // fails outright) means the session is genuinely dead. Log the decision
  // (endpoint path only — never query strings / PII) and sign out. signOut()
  // emits a Hub "signedOut" event which AuthContext consumes to perform the
  // single resetAuth()/redirect.
  const handleDeadSession = async (endpoint: string) => {
    let path = endpoint;
    try {
      path = new URL(endpoint).pathname;
    } catch {
      // endpoint may be relative or malformed; strip any query manually
      path = endpoint.split("?")[0];
    }
    Sentry.captureMessage("auth.session_expired_logout", {
      level: "warning",
      tags: { endpoint: path, status: 401 },
    });
    try {
      await signOut();
    } catch (error) {
      // signOut() normally emits the Hub "signedOut" event that AuthContext
      // consumes to reset the UI and redirect. If signOut() itself fails (e.g.
      // a network blip while revoking the token), that event never fires and
      // the user would be stranded on a broken authed page. Emit it ourselves
      // so AuthContext still tears down the confirmed-dead session.
      Sentry.captureMessage("auth.signout_failed", {
        level: "warning",
        tags: { errorName: (error as { name?: string })?.name ?? "unknown" },
      });
      Hub.dispatch("auth", { event: "signedOut" });
    }
  };

  // NOTE: this is the per-render implementation. The exported `fetchWithAuth`
  // (defined right after) is an identity-STABLE wrapper — see comment there.
  const fetchWithAuthImpl = async (endpoint: string, options: any = {}) => {
    // One id per logical call — the 401 retry below reuses it.
    const requestId = createRequestId();

    // Build headers + fire the request for a given token. Kept as an inner
    // helper so we can replay the exact same request once after a refresh.
    const doFetch = (token: string) => {
      // For FormData, don't set Content-Type - let browser set
      // multipart/form-data with boundary
      const isFormData = options.body instanceof FormData;

      const headers: Record<string, string> = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        "X-Client-Session-Id": getClientSessionId(),
        [REQUEST_ID_HEADER]: requestId,
        ...actingLocationHeaders(),
      };

      // Only set Content-Type if it's not FormData and not already specified in options.headers
      if (!isFormData && !options.headers?.["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }

      return fetch(endpoint, {
        ...options,
        headers,
      });
    };

    const token = await getIdToken();
    if (!token) {
      // A null here can be a transient fetchAuthSession failure while the
      // 30-day refresh token is still valid — do NOT sign out (that would be
      // the exact "discard a still-valid session" bug). Throw so the caller
      // sees an error; a genuinely dead/expired refresh token is caught
      // authoritatively by the Amplify Hub "tokenRefresh_failure" listener in
      // AuthContext. The authoritative API-layer logout is the surviving-401
      // path below.
      throw new Error("No authentication token available");
    }

    let response = await doFetch(token);

    // On a 401, force exactly one token refresh + one retry. Only a 401 that
    // survives that (or a refresh that fails) logs the user out.
    if (response.status === 401) {
      Sentry.addBreadcrumb({
        category: "auth",
        level: "warning",
        message: "401 received — forcing token refresh and retrying once",
      });
      loopLog("fetchWithAuth 401 -> token refresh", endpoint);
      const fresh = await refreshToken(true);
      if (fresh) {
        response = await doFetch(fresh);
      }
      if (!fresh || response.status === 401) {
        await handleDeadSession(endpoint);
        // Fall through to the existing !response.ok handling so callers still
        // receive a thrown error.
      }
    }

    // Get response content type
    const responseContentType = response.headers.get("content-type") || "";

    if (!response.ok) {
      loopLog(`fetchWithAuth non-OK ${response.status}`, endpoint);
      // Handle error responses based on content type; non-JSON errors
      // (e.g., text/html) carry the raw text as errorBody.
      const isJsonError = responseContentType.includes("application/json");
      const errorBody = isJsonError
        ? await response.json()
        : await response.text();
      const error = new Error(
        (isJsonError && (errorBody?.message || errorBody?.error)) ||
          "Failed to fetch data",
      );
      (error as any).status = response.status;
      (error as any).errorBody = errorBody;
      // Lets callers/support quote the id that finds this call's backend logs.
      (error as any).requestId = requestId;
      recordFailedRequest(requestId, response.status, endpoint);
      throw error;
    }

    // Handle successful responses based on content type
    if (responseContentType.includes("application/json")) {
      const text = await response.text();
      if (text.trim()) {
        return JSON.parse(text);
      }
      return null;
    } else if (
      responseContentType.includes("text/html") ||
      responseContentType.includes("text/plain")
    ) {
      // Return text content for HTML or plain text responses
      return await response.text();
    }

    // Return null for empty responses (common with DELETE operations)
    return null;
  };

  // Identity-STABLE fetchWithAuth (fix for the multi-location infinite-request
  // loop, 2026-07-23). The implementation above is a plain closure recreated on
  // every AppProvider render, and AppProvider re-renders on every token-state
  // change (mount refresh, forced refresh after a 401 — each mints a distinct
  // JWT). Handing that unstable identity to consumers put it in the dependency
  // chain of ~every data-fetch useEffect in the app (via useCallbacks like
  // getStaffCommissions/[fetchWithAuth]), so ONE AppProvider re-render refired
  // EVERY mounted fetch effect — a full-app request burst; any failure in the
  // burst that minted another token looped it forever. The ref indirection
  // (same pattern as refreshTokenRef above) keeps the identity constant for the
  // provider's lifetime while always calling the freshest closure.
  const fetchWithAuthRef = useRef(fetchWithAuthImpl);
  fetchWithAuthRef.current = fetchWithAuthImpl;
  const fetchWithAuth = useCallback(
    (endpoint: string, options: any = {}) =>
      fetchWithAuthRef.current(endpoint, options),
    [],
  );

  const getSalons = async () => {
    const responseJson = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALONS, "", null),
    );
    // Backend returns 200 with {} when user has no salon (e.g. right after creation)
    if (isEmptyObject(responseJson)) {
      recordApiEmptyResponseWarning(
        "GET /business-locations",
        "Empty object (user has no salon yet)",
      );
      return [];
    }
    return responseJson;
  };

  // Fetch one salon record by uuid (GET /business-locations/{salon_uuid}). Used by the
  // multi-location current-location resolution (utils/currentLocation.ts):
  // plain GET /business-locations follows the legacy salon_staff link, which does NOT
  // track last_used_salon_id, so after a location switch it returns the OLD
  // salon. NOTE: the backend query may return extra records (it also matches
  // the caller's salon_staff row) — callers must pick by uuid via
  // pickSalonRecord, not [0].
  const getSalonByUuid = async (salonUuid: string) => {
    const responseJson = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALON_BY_ID, "", { salonId: salonUuid }),
    );
    if (isEmptyObject(responseJson)) {
      recordApiEmptyResponseWarning(
        "GET /business-locations/{uuid}",
        "Empty object (salon not found)",
      );
      return [];
    }
    return responseJson;
  };

  // Fetch services
  const getServices = async (salonId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICES_BY_SALON, "", { salonId }),
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Create services
  const createServices = async (servicesData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICES, undefined, null),
        {
          method: "POST",
          body: JSON.stringify(servicesData),
        },
      );

      return responseJson;
    } catch (error) {
      if (
        error instanceof Error &&
        error.message &&
        error.message.includes("(Status Code:")
      ) {
        error.message = error.message.replace(/\s*\(Status Code: \d+\)/, "");
      }
      throw error;
    }
  };

  // Update services
  const updateServices = async (servicesData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICES, "", null),
        {
          method: "PUT",
          body: JSON.stringify(servicesData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Bulk-set the business charge on many services and/or products in one
  // set-based backend update. Callers refresh the services/products contexts
  // afterward to keep local state in sync.
  const bulkUpdateBusinessCharges = async (
    businessLocationId: string,
    payload: {
      services?: { id: string; default_business_charge: number }[];
      products?: { id: string; business_charge: number }[];
    },
  ) => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SET_BUSINESS_CHARGES, "", {
        businessLocationId,
      }),
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  };

  // Delete salon service
  const deleteSalonService = async (salonServiceId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_SERVICES, "", { salonServiceId }),
        {
          method: "DELETE",
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // ==========================================
  // POLICIES (salon booking policies)
  // ==========================================

  // Fetch all policies for a salon
  const getSalonPolicies = async (salonId: string): Promise<Policy[]> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_POLICIES, "", { salonId }),
      );
      return responseJson ?? [];
    } catch (error) {
      throw error;
    }
  };

  // Fetch preset/default policies (used by the "Add Policy" picker)
  const getPolicyDefaults = async (): Promise<DefaultPolicy[]> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.POLICIES_DEFAULTS, "", null),
      );
      return responseJson ?? [];
    } catch (error) {
      throw error;
    }
  };

  // Create a new policy
  const createPolicy = async (
    policyData: CreatePolicyRequest,
  ): Promise<Policy> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.POLICIES, "", null),
        {
          method: "POST",
          body: JSON.stringify(policyData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update an existing policy (id in the body; backend route is PUT /policies)
  const updatePolicy = async (
    policyData: UpdatePolicyRequest,
  ): Promise<Policy> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.POLICIES, "", null),
        {
          method: "PUT",
          body: JSON.stringify(policyData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Delete a policy
  const deletePolicy = async (policyId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.POLICY_BY_ID, "", { policyId }),
        {
          method: "DELETE",
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Delete staff service
  const deleteStaffService = async (staffServiceId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.STAFF_SERVICES, "", { staffServiceId }),
        {
          method: "DELETE",
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const getTransaction = async (transactionId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.TRANSACTIONS,
          `bookingId=${transactionId}`,
          null,
        ),
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Booking-flow redesign: server-computed suggested start times for a set of
  // services (in appointment order) on a given salon-local date. Comma-joins
  // service ids; staffIds is aligned 1:1 with services ("" = any provider);
  // startTime is the requested first-service start ("HH:mm" salon-local);
  // timezone is an IANA name (e.g. "America/New_York").
  const getSuggestedTimes = async ({
    services,
    staffIds,
    startTime,
    date,
    timezone,
  }: {
    services: string[];
    staffIds?: string[];
    startTime?: string;
    date: string;
    timezone: string;
  }) => {
    try {
      const queryParts = [
        `services=${services.map(encodeURIComponent).join(",")}`,
        `date=${encodeURIComponent(date)}`,
        `timezone=${encodeURIComponent(timezone)}`,
      ];
      if (staffIds && staffIds.some(Boolean)) {
        queryParts.push(
          `staff_ids=${staffIds.map(encodeURIComponent).join(",")}`,
        );
      }
      if (startTime) {
        queryParts.push(`start_time=${encodeURIComponent(startTime)}`);
      }

      const responseJson = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.SUGGESTED_TIMES,
          queryParts.join("&"),
          null,
        ),
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const getTransactions = async (salonId: string, queryParams: string = "") => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTIONS_BY_SALON, queryParams, {
          salonId,
        }),
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Sales tab (BE-1) list fetch. Normalizes to the same array-of-
  // GetTransactionsResponse shape the transactions endpoint returns.
  //
  // `GET /business_locations/{id}/sales?view=` (BE-1) is live; the fallback to
  // the legacy transactions endpoint stays only as a safety net. Until then we fall back to
  // the existing transactions endpoint so the list renders against dev — it
  // lacks refunds[], per-payment refunded_amount, and is_future_deposit_only.
  const getSales = async (
    businessLocationId: string,
    view: "transactions" | "purchases" = "transactions",
    queryParams: string = "",
  ) => {
    const USE_SALES_ENDPOINT = true;
    try {
      if (USE_SALES_ENDPOINT) {
        const viewQuery = `view=${view}`;
        const combined = queryParams
          ? `${viewQuery}&${queryParams}`
          : viewQuery;
        return await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SALES_BY_BUSINESS_LOCATION, combined, {
            businessLocationId,
          }),
        );
      }
      // Fallback: existing transactions endpoint (keyed by salon/business
      // location id). Returns { transactions: [...] }.
      return await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTIONS_BY_SALON, queryParams, {
          salonId: businessLocationId,
        }),
      );
    } catch (error) {
      throw error;
    }
  };

  const getClient = async (clientId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENTS_BY_ID, "", {
          clientId,
        }),
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const addLineItemToTransaction = async (
    transactionId: string,
    lineItemData: any,
  ) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.ADD_LINE_ITEM_TO_TRANSACTION, "", null),
        {
          method: "POST",
          body: JSON.stringify({
            transactionId,
            line_items: lineItemData,
          }),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Create appointment
  const createAppointment = async (appointmentData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS, "", null),
        {
          method: "POST",
          body: JSON.stringify(appointmentData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update appointment
  const updateAppointment = async (appointmentData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.APPOINTMENTS, "", null),
        {
          method: "PUT",
          body: JSON.stringify(appointmentData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const createTimeBlock = async (timeBlockData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TIME_BLOCKS, "", null),
        {
          method: "POST",
          body: JSON.stringify(timeBlockData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const updateTimeBlock = async (timeBlockData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TIME_BLOCKS, "", null),
        {
          method: "PUT",
          body: JSON.stringify(timeBlockData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const getTimeBlocks = async (salonId: string, queryParams = "") => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TIME_BLOCKS_BY_SALON, queryParams, {
          salonId,
        }),
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const deleteTimeBlock = async (timeBlockId: string, appliesTo: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DELETE_TIME_BLOCKS, "", {
          timeOffId: timeBlockId,
          appliesTo,
        }),
        {
          method: "DELETE",
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Create salon
  const createSalon = async (salonData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALONS, "", null),
        {
          method: "POST",
          body: JSON.stringify(salonData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update salon
  const updateSalon = async (salonData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALONS, "", null),
        {
          method: "PUT",
          body: JSON.stringify(salonData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Get salon staff
  const getSalonStaff = async (salonId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_STAFF, "", { salonId }),
      );
      // Backend returns 200 with [] when owner/manager has no salon_id yet
      if (Array.isArray(responseJson) && responseJson.length === 0) {
        recordApiEmptyResponseWarning(
          "GET /salon-staff",
          "Empty array (no salon or no staff yet)",
        );
      }
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update staff
  const updateStaff = async (employeeData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.STAFF, "", null),
        {
          method: "PUT",
          body: JSON.stringify(employeeData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Delete staff
  const deleteStaff = async (staffId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.STAFF_BY_ID, "", { staffId }),
        {
          method: "DELETE",
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Invite staff
  const inviteStaff = async (employeeData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.INVITE_STAFF, "", null),
        {
          method: "POST",
          body: JSON.stringify(employeeData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // --- Staff ad-hoc working-hours overrides ("Schedule Adjustments") ---------
  // Upsert by (staff, date); the save always succeeds (no appointment-conflict
  // guard). Errors throw via fetchWithAuth like any other call.
  const createStaffHoursOverride = async (
    body: CreateStaffHoursOverrideBody,
  ): Promise<StaffHoursOverride> => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.STAFF_HOURS_OVERRIDES, "", null),
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
  };

  const deleteStaffHoursOverride = async (overrideId: string) => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.STAFF_HOURS_OVERRIDE_BY_ID, "", {
        overrideId,
      }),
      { method: "DELETE" },
    );
  };

  const getStaffHoursOverrides = async (
    staffId: string,
    params: GetStaffHoursOverridesParams = {},
  ): Promise<StaffHoursOverride[]> => {
    const query = new URLSearchParams();
    if (params.businessLocationUuid) {
      query.set("business_location_uuid", params.businessLocationUuid);
    }
    if (params.startDate) query.set("start_date", params.startDate);
    if (params.endDate) query.set("end_date", params.endDate);
    const responseJson = await fetchWithAuth(
      getApiUrl(
        API_CONFIG.ENDPOINTS.STAFF_HOURS_OVERRIDES_BY_STAFF,
        query.toString(),
        {
          staffId,
        },
      ),
    );
    return Array.isArray(responseJson) ? responseJson : [];
  };

  const getStaffHoursOverrideEffective = async (
    staffId: string,
    date: string,
    businessLocationUuid?: string,
  ): Promise<EffectiveStaffHours> => {
    const query = new URLSearchParams({ date });
    if (businessLocationUuid) {
      query.set("business_location_uuid", businessLocationUuid);
    }
    return await fetchWithAuth(
      getApiUrl(
        API_CONFIG.ENDPOINTS.STAFF_HOURS_OVERRIDES_EFFECTIVE,
        query.toString(),
        { staffId },
      ),
    );
  };

  // All staff's overrides for a salon in a date range (one query). Powers the
  // calendar day-view working-hours shading overlay.
  const getSalonHoursOverrides = async (
    businessLocationUuid: string,
    startDate: string,
    endDate: string,
  ): Promise<StaffHoursOverride[]> => {
    const query = new URLSearchParams();
    if (startDate) query.set("start_date", startDate);
    if (endDate) query.set("end_date", endDate);
    const responseJson = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALON_HOURS_OVERRIDES, query.toString(), {
        businessLocationId: businessLocationUuid,
      }),
    );
    return Array.isArray(responseJson) ? responseJson : [];
  };

  // Get checkout URL
  const getCheckoutUrl = async (body: any) => {
    try {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_SUBSCRIBE_URL, "", null),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Fetch live Stripe pricing for a subscription tier. Used to display the
  // current price for tiers with variable per-salon pricing (MedSpa Essentials).
  // Memoized so consumers using it in `useEffect` deps don't loop on every
  // AppProvider render.
  const getSubscriptionPricing = useCallback(
    async (tier: SubscriptionTierType): Promise<SubscriptionTierPricing> => {
      return await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_SUBSCRIPTION_PRICING, "", { tier }),
        { method: "GET" },
      );
    },
    [fetchWithAuth],
  );

  // Create Stripe Connect account
  const createStripeConnectAccount = async (
    businessInfo: any,
    currentUser: any,
  ) => {
    try {
      // Construct phone number - use businessInfo if available, otherwise fallback to currentUser
      let phone: string | undefined;

      // Priority 1: Use businessInfo phone fields if all are present
      if (
        businessInfo?.country_code &&
        businessInfo?.area_code &&
        businessInfo?.phone_number
      ) {
        phone = `+${businessInfo.country_code}${businessInfo.area_code}${businessInfo.phone_number}`;
      }
      // Priority 2: Use businessInfo phone_number if it's already in E.164 format
      else if (businessInfo?.phone_number) {
        const phoneStr = String(businessInfo.phone_number);
        if (phoneStr.startsWith("+")) {
          phone = phoneStr;
        }
      }
      // Priority 3: Use currentUser's area_code and phone_number (from API)
      else if (currentUser?.area_code && currentUser?.phone_number) {
        phone = `+1${currentUser.area_code}${currentUser.phone_number}`;
      }
      // Priority 4: Use currentUser's phone_number if it's in E.164 format (from Cognito)
      else if (currentUser?.phone_number) {
        const phoneStr = String(currentUser.phone_number);
        if (phoneStr.startsWith("+")) {
          phone = phoneStr;
        } else {
          // Try to format it - remove non-digits and add +1 if needed
          const digitsOnly = phoneStr.replace(/\D/g, "");
          if (digitsOnly.length === 10) {
            phone = `+1${digitsOnly}`;
          } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
            phone = `+${digitsOnly}`;
          }
        }
      }

      // If still no phone, throw an error
      if (!phone) {
        throw new Error(
          "Phone number is required for Stripe Connect account. Please update your business information with a phone number.",
        );
      }

      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_STRIPE_CONNECT_ACCOUNT, "", null),
        {
          method: "POST",
          body: JSON.stringify({
            salonId: businessInfo.id,
            salonName: businessInfo.salon_name,
            email: currentUser.email,
            phone,
            addressLine1: businessInfo.street_address_1,
            addressLine2: businessInfo.street_address_2,
            city: businessInfo.city,
            state: businessInfo.state,
            zipCode: businessInfo.zip_code,
          }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Get Stripe Connect account link
  const getStripeConnectAccountLink = async (connectedAccountId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.GET_STRIPE_CONNECT_ACCOUNT_LINK,
          "",
          null,
        ),
        {
          method: "POST",
          body: JSON.stringify({ accountId: connectedAccountId }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Initialize Stripe Connect account session
  const initializeStripeConnectAccountSession = async (
    connectedAccountId: string,
  ) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.INITIALIZE_STRIPE_CONNECT_ACCOUNT_SESSION,
          "",
          null,
        ),
        {
          method: "POST",
          body: JSON.stringify({ accountId: connectedAccountId }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Register Stripe Connect terminal device
  const registerStripeConnectTerminalDevice = async (
    deviceData: any,
    businessInfo: any,
  ) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.REGISTER_STRIPE_TERMINAL_DEVICE,
          "",
          null,
        ),
        {
          method: "POST",
          body: JSON.stringify({
            ...deviceData,
            salonId: businessInfo?.id,
            businessLocationId: businessInfo?.id,
            locationName: businessInfo?.salon_name,
            accountId: businessInfo?.stripe_connect_account_id,
            addressLine1: businessInfo.street_address_1,
            addressLine2: businessInfo.street_address_2,
            city: businessInfo.city,
            state: businessInfo.state,
            zipCode: businessInfo.zip_code,
          }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Get Stripe Connect terminal device
  const getStripeConnectTerminalDevice = async (
    deviceId: string,
    accountId: string,
  ) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_STRIPE_TERMINAL_DEVICE, "", null),
        {
          method: "POST",
          body: JSON.stringify({ deviceId, accountId }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Remove Stripe Terminal device
  const removeStripeTerminalDevice = async (
    deviceId: string,
    accountId: string,
    businessLocationId: string,
  ) => {
    const responseJson = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.REMOVE_STRIPE_TERMINAL_DEVICE, "", null),
      {
        method: "POST",
        body: JSON.stringify({ deviceId, accountId, businessLocationId }),
      },
    );
    return responseJson;
  };

  // List a location's payment terminals (up to 3 per location)
  const getPaymentTerminals = async (businessLocationId: string) => {
    return await fetchWithAuth(
      getApiUrl(
        API_CONFIG.ENDPOINTS.PAYMENT_TERMINALS,
        `businessLocationId=${encodeURIComponent(businessLocationId)}`,
        null,
      ),
      { method: "GET" },
    );
  };

  // Rename a payment terminal. The name IS the Stripe reader label; the backend
  // updates Stripe first (source of truth) then syncs our cached copy.
  const updatePaymentTerminalLabel = async (
    terminalId: string,
    businessLocationId: string,
    accountId: string,
    label: string,
  ) => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.PAYMENT_TERMINAL_BY_ID, "", {
        terminalId,
      }),
      {
        method: "PUT",
        body: JSON.stringify({ businessLocationId, accountId, label }),
      },
    );
  };

  // Set terminal configuration (tipping & splashscreen)
  const setTerminalConfiguration = async (configData: any) => {
    const responseJson = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SET_TERMINAL_CONFIGURATION, "", null),
      {
        method: "POST",
        body: JSON.stringify(configData),
      },
    );
    return responseJson;
  };

  // Create payment intent
  const createPaymentIntent = async (paymentIntentData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_PAYMENT_INTENT, "", null),
        {
          method: "POST",
          body: JSON.stringify(paymentIntentData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update payment intent
  const updatePaymentIntent = async (paymentIntentData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.UPDATE_PAYMENT_INTENT, "", null),
        {
          method: "POST",
          body: JSON.stringify(paymentIntentData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Cancel payment intent
  const cancelPaymentIntent = async (paymentIntentData: {
    paymentIntentId: string;
    terminalDeviceId: string;
    accountId: string;
  }) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CANCEL_PAYMENT_INTENT, "", null),
        {
          method: "POST",
          body: JSON.stringify({
            paymentIntentId: paymentIntentData.paymentIntentId,
            terminalDeviceId: paymentIntentData.terminalDeviceId,
            accountId: paymentIntentData.accountId,
          }),
        },
      );
      return responseJson;
    } catch (error) {
      if (
        (error as any).message &&
        (error as any).message.includes(
          "You cannot cancel this PaymentIntent because it has a status of canceled. Only a PaymentIntent with one of the following statuses may be canceled: requires_payment_method, requires_capture, requires_confirmation, requires_action, processing.",
        )
      ) {
        console.warn("Payment intent already canceled");
      } else {
      }
    }
  };

  // Process payment
  const processPayment = async (paymentIntentData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.PROCESS_PAYMENT, "", null),
        {
          method: "POST",
          body: JSON.stringify(paymentIntentData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Simulate payment
  const simulatePayment = async (paymentIntentData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SIMULATE_PAYMENT, "", null),
        {
          method: "POST",
          body: JSON.stringify(paymentIntentData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Poll Stripe payment status
  const pollStripePaymentStatus = async (
    transactionId: string,
    paymentIntentId: string,
  ) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.POLL_STRIPE_PAYMENT_STATUS, "", null),
        {
          method: "POST",
          body: JSON.stringify({ transactionId, paymentIntentId }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Create quick sale - products only transaction with immediate payment
  const createQuickSale = async (
    quickSaleData: CreateQuickSaleRequest,
  ): Promise<CreateQuickSaleResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_QUICK_SALE, "", null),
        {
          method: "POST",
          body: JSON.stringify(quickSaleData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // ==========================================
  // Card-on-File Methods
  // ==========================================

  // Create a SetupIntent for adding a card to a client
  const createSetupIntent = async (
    request: CreateSetupIntentRequest,
  ): Promise<CreateSetupIntentResponse> => {
    try {
      // Admin portal action: requires authenticated request
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_SETUP_INTENT, "", {
          clientId: request.clientId,
        }),
        {
          method: "POST",
          body: JSON.stringify({ salonId: request.salonId }),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Get all payment methods for a client
  const getClientPaymentMethods = async (
    clientId: string,
  ): Promise<GetClientPaymentMethodsResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_PAYMENT_METHODS, "", {
          clientId,
        }),
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Delete a payment method from a client
  const deleteClientPaymentMethod = async (
    clientId: string,
    pmId: number,
  ): Promise<DeletePaymentMethodResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_PAYMENT_METHOD_BY_ID, "", {
          clientId,
          pmId: pmId.toString(),
        }),
        {
          method: "DELETE",
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Set a client's default payment method
  const setDefaultClientPaymentMethod = async (
    clientId: string,
    pmId: number,
  ): Promise<SetDefaultClientPaymentMethodResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_PAYMENT_METHOD_DEFAULT, "", {
          clientId,
          pmId: pmId.toString(),
        }),
        {
          method: "PUT",
        },
      );
      return responseJson as SetDefaultClientPaymentMethodResponse;
    } catch (error) {
      throw error;
    }
  };

  // Set/clear a client's payment method nickname
  const setClientPaymentMethodNickname = async (
    clientId: string,
    pmId: number,
    nickname: string,
  ): Promise<SetClientPaymentMethodNicknameResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_PAYMENT_METHOD_NICKNAME, "", {
          clientId,
          pmId: pmId.toString(),
        }),
        {
          method: "PUT",
          body: JSON.stringify({ nickname }),
        },
      );
      return responseJson as SetClientPaymentMethodNicknameResponse;
    } catch (error) {
      throw error;
    }
  };

  // Charge a card on file for a transaction
  const chargeCardOnFile = async (
    request: ChargeCardOnFileRequest,
  ): Promise<ChargeCardOnFileResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CHARGE_CARD_ON_FILE, "", null),
        {
          method: "POST",
          body: JSON.stringify(request),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const refundPayment = async (
    request: RefundPaymentRequest,
  ): Promise<RefundPaymentResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.REFUND_PAYMENT, "", null),
        {
          method: "POST",
          body: JSON.stringify(request),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Email a Stripe receipt for a card-backed payment. `email` is optional —
  // when omitted the backend emails the payment's linked address.
  const sendReceipt = async (
    paymentId: string,
    email?: string,
  ): Promise<SendReceiptResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SEND_RECEIPT, "", { paymentId }),
        {
          method: "POST",
          body: JSON.stringify(email ? { email } : {}),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  const getNotifications = async (
    page = 1,
    size = 20,
    filters?: NotificationFilters,
  ) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
      });
      if (filters?.event_name) params.set("event_name", filters.event_name);
      if (typeof filters?.is_read === "boolean") {
        params.set("is_read", String(filters.is_read));
      }
      const queryParams = params.toString();
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.NOTIFICATIONS, queryParams, null),
      );

      // Backend returns 200 with empty notifications when user has no salon
      const notifications = responseJson?.notifications;
      const total = responseJson?.pagination?.total;
      if (
        Array.isArray(notifications) &&
        notifications.length === 0 &&
        total === 0
      ) {
        recordApiEmptyResponseWarning(
          "GET /notifications",
          "Empty list (user has no salon yet)",
        );
      }
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update notification read status
  const updateNotificationReadStatus = async (notifications: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.NOTIFICATIONS, "", null),
        {
          method: "PUT",
          body: JSON.stringify(notifications),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Settings -> Notifications: per-staff notification preferences. The
  // backend scopes the response by settingsNotifications (own vs all).
  const getNotificationPreferences =
    useCallback(async (): Promise<NotificationPreferencesResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.NOTIFICATION_PREFERENCES, "", null),
      );
    }, [fetchWithAuth]);

  const updateNotificationPreferences = useCallback(
    async (
      body: UpdateNotificationPreferencesRequest,
    ): Promise<UpdateNotificationPreferencesResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.NOTIFICATION_PREFERENCES, "", null),
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  // Get deposit configs
  const getDepositConfigs = async (salonId: string) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DEPOSIT_CONFIGS, "", { salonId }),
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Create deposit config
  const createDepositConfig = async (depositData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DEPOSIT_CONFIGS, "", {
          salonId: depositData.salon_id,
        }),
        {
          method: "POST",
          body: JSON.stringify(depositData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Update deposit config
  const updateDepositConfig = async (depositData: any) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DEPOSIT_CONFIGS, "", {
          salonId: depositData.salon_id,
        }),
        {
          method: "PUT",
          body: JSON.stringify(depositData),
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Delete deposit config
  const deleteDepositConfig = async (
    salonId: string,
    depositConfigId: string,
  ) => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DEPOSIT_CONFIG_BY_ID, "", {
          salonId,
          depositConfigId,
        }),
        {
          method: "DELETE",
        },
      );
      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // ==========================================
  // Off-Platform Payment Methods (per-salon catalog + selection)
  // ==========================================
  // All four endpoints now share the {payment_methods: [...]} envelope
  // shape. ``getSalonPaymentMethods`` unwraps so consumers can keep the
  // simpler ``SalonPaymentMethod[]`` return type. The wrappers are
  // memoized via useCallback to match the convention in the newer
  // sections of this file (Rollfi / time-tracking / commissions, see
  // ~line 2192+) so consumers can include them in useEffect deps
  // without paying for a re-fetch on every AppProvider render.

  const getSalonPaymentMethods = useCallback(
    async (salonId: string): Promise<SalonPaymentMethod[]> => {
      const response = (await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_PAYMENT_METHODS, "", { salonId }),
      )) as SalonPaymentMethodsListResponse;
      return response?.payment_methods ?? [];
    },
    [fetchWithAuth],
  );

  const addSalonPaymentMethod = useCallback(
    async (
      salonId: string,
      name: string,
    ): Promise<AddSalonPaymentMethodResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_PAYMENT_METHODS, "", { salonId }),
        {
          method: "POST",
          body: JSON.stringify({ name }),
        },
      );
    },
    [fetchWithAuth],
  );

  const deleteSalonPaymentMethod = useCallback(
    async (
      salonId: string,
      paymentMethodId: string,
    ): Promise<SalonPaymentMethodsListResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_PAYMENT_METHOD_BY_ID, "", {
          salonId,
          paymentMethodId,
        }),
        {
          method: "DELETE",
        },
      );
    },
    [fetchWithAuth],
  );

  const setSalonPaymentMethodsSelection = useCallback(
    async (
      salonId: string,
      selectedIds: string[],
    ): Promise<SalonPaymentMethodsListResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_PAYMENT_METHODS_SELECTION, "", {
          salonId,
        }),
        {
          method: "PUT",
          body: JSON.stringify({ selected_ids: selectedIds }),
        },
      );
    },
    [fetchWithAuth],
  );

  // ==========================================
  // Discount Presets (per-location catalog)
  // ==========================================
  // POST/PUT wrap the preset in {discount_preset}; GET wraps the list in
  // {discount_presets}. The wrappers unwrap so consumers (the shared
  // DiscountPresetsContext) work with plain DiscountPreset values. Memoized
  // like the Off-Platform Payments wrappers above so they're safe in deps.

  const getDiscountPresets = useCallback(
    async (salonId: string): Promise<DiscountPreset[]> => {
      const response = (await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_DISCOUNT_PRESETS, "", {
          salonId,
        }),
      )) as DiscountPresetsListResponse | null;
      return response?.discount_presets ?? [];
    },
    [fetchWithAuth],
  );

  const createDiscountPreset = useCallback(
    async (
      salonId: string,
      body: CreateDiscountPresetRequest,
    ): Promise<DiscountPreset> => {
      const response = (await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SALON_DISCOUNT_PRESETS, "", {
          salonId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      )) as DiscountPresetResponse;
      return response.discount_preset;
    },
    [fetchWithAuth],
  );

  const updateDiscountPreset = useCallback(
    async (
      salonId: string,
      presetId: string,
      body: UpdateDiscountPresetRequest,
    ): Promise<DiscountPreset> => {
      const response = (await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DISCOUNT_PRESET_BY_ID, "", {
          salonId,
          presetId,
        }),
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      )) as DiscountPresetResponse;
      return response.discount_preset;
    },
    [fetchWithAuth],
  );

  const deleteDiscountPreset = useCallback(
    async (
      salonId: string,
      presetId: string,
    ): Promise<DeleteDiscountPresetResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DISCOUNT_PRESET_BY_ID, "", {
          salonId,
          presetId,
        }),
        {
          method: "DELETE",
        },
      );
    },
    [fetchWithAuth],
  );

  // Replaces the location's ordered quick discounts; returns every active
  // preset (the GET shape) so the catalog refreshes in one round trip.
  const setQuickDiscounts = useCallback(
    async (
      businessLocationId: string,
      presetIds: string[],
    ): Promise<DiscountPreset[]> => {
      const body: SetQuickDiscountsRequest = { discount_preset_ids: presetIds };
      const response = (await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DISCOUNT_PRESETS_QUICK_DISCOUNTS, "", {
          businessLocationId,
        }),
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      )) as DiscountPresetsListResponse;
      return response.discount_presets;
    },
    [fetchWithAuth],
  );

  // ==========================================
  // Tax Rates (Phase 2 admin UI)
  // ==========================================

  const getTaxRates = async (salonId: string) => {
    return fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALON_TAX_RATES, "", { salonId }),
    );
  };

  const createTaxRate = async (salonId: string, data: any) => {
    return fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALON_TAX_RATES, "", { salonId }),
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  };

  const updateTaxRate = async (taxRateId: string, data: any) => {
    return fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.TAX_RATE_BY_ID, "", { taxRateId }),
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
  };

  /**
   * Delete a tax rate. Defaults to a soft delete (backend end-dates the row).
   * Pass `force=true` to hard-delete, which cascades through junctions and
   * line_item_taxes audit rows — only use for rates created by mistake.
   */
  const deleteTaxRate = async (
    taxRateId: string,
    options: { force?: boolean } = {},
  ) => {
    const suffix = options.force ? "?force=true" : "";
    return fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.TAX_RATE_BY_ID, "", { taxRateId }) +
        suffix,
      {
        method: "DELETE",
      },
    );
  };

  /**
   * Replace the set of tax rates applied to a single product. Pass
   * `taxRateIds: []` to clear all junctions for that product.
   */
  const setProductTaxRates = async (
    productId: string,
    taxRateIds: number[],
  ) => {
    return fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.PRODUCT_TAX_RATES, "", { productId }),
      {
        method: "PATCH",
        body: JSON.stringify({ tax_rate_ids: taxRateIds }),
      },
    );
  };

  /**
   * Replace the set of tax rates applied to a single salon service. Pass
   * `taxRateIds: []` to clear all junctions.
   */
  const setSalonServiceTaxRates = async (
    salonId: string,
    salonServiceId: string,
    taxRateIds: number[],
  ) => {
    return fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.SALON_SERVICE_TAX_RATES, "", {
        salonId,
        salonServiceId,
      }),
      {
        method: "PATCH",
        body: JSON.stringify({ tax_rate_ids: taxRateIds }),
      },
    );
  };

  // Edit transaction
  const editTransaction = async (
    transactionData: EditTransactionRequest,
  ): Promise<EditTransactionResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTION, "", null),
        {
          method: "PUT",
          body: JSON.stringify(transactionData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Edit transaction line items
  const editTransactionLineItems = async (
    transactionData: EditTransactionLineItemsRequest,
  ): Promise<EditTransactionLineItemsResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTION_LINE_ITEMS, "", null),
        {
          method: "PUT",
          body: JSON.stringify(transactionData),
        },
      );

      return responseJson;
    } catch (error) {
      throw error;
    }
  };

  // Process non-card payment
  const processNonCardPayment = async (
    nonCardPaymentData: ProcessNonCardPaymentRequest,
  ): Promise<ProcessNonCardPaymentResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.PROCESS_NON_CARD_PAYMENT, "", null),
        {
          method: "POST",
          body: JSON.stringify(nonCardPaymentData),
        },
      );
      return responseJson as ProcessNonCardPaymentResponse;
    } catch (error) {
      throw error;
    }
  };

  // ==========================================
  // Cash Drawer
  // ==========================================
  const getCashDrawerOverview = async (
    salonId: string,
    options?: { ensureOpen?: boolean },
  ) => {
    const query = options?.ensureOpen ? "ensure_open=true" : "";
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CASH_DRAWER_SESSIONS, query, { salonId }),
      { method: "GET" },
    );
  };

  const getCashDrawerSession = async (salonId: string, sessionId: string) => {
    return await fetchWithAuth(
      getApiUrl(
        API_CONFIG.ENDPOINTS.CASH_DRAWER_SESSIONS,
        `session_id=${encodeURIComponent(sessionId)}`,
        { salonId },
      ),
      { method: "GET" },
    );
  };

  const countCashDrawer = async (
    sessionId: string,
    payload: CountDrawerRequest,
  ) => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CASH_DRAWER_SESSION_COUNT, "", {
        sessionId,
      }),
      { method: "POST", body: JSON.stringify(payload) },
    );
  };

  const recordCashAdjustment = async (
    sessionId: string,
    payload: CreateCashTransactionRequest,
  ) => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CASH_DRAWER_SESSION_TRANSACTIONS, "", {
        sessionId,
      }),
      { method: "POST", body: JSON.stringify(payload) },
    );
  };

  const voidCashTransaction = async (transactionId: string) => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CASH_TRANSACTION_VOID, "", {
        transactionId,
      }),
      { method: "PATCH" },
    );
  };

  const updateCashDrawerBalancingDate = async (
    sessionId: string,
    balancingDate: string,
  ): Promise<UpdateBalancingDateResponse> => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CASH_DRAWER_SESSION_BALANCING_DATE, "", {
        sessionId,
      }),
      {
        method: "PATCH",
        body: JSON.stringify({ balancing_date: balancingDate }),
      },
    );
  };

  const getCashDrawerBalancingSummary = async (
    sessionId: string,
  ): Promise<CashDrawerBalancingSummaryResponse> => {
    return await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CASH_DRAWER_BALANCING_SUMMARY, "", {
        sessionId,
      }),
      { method: "GET" },
    );
  };

  const uploadImage = async (
    request: ImageUploadRequest,
  ): Promise<ImageUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("image", request.image);
      // The SSG /create-image endpoint requires `business_location_id` (the salon
      // UUID) after the salons → business_locations rename.
      formData.append("business_location_id", request.business_location_id);

      if (request.optimization_type) {
        formData.append("optimization_type", request.optimization_type);
      }

      if (request.content_type) {
        formData.append("content_type", request.content_type);
      }

      if (request.image_type) {
        formData.append("image_type", request.image_type);
      }

      if (request.staff_id) {
        formData.append("staff_id", request.staff_id);
      }

      // Use fetchWithAuth which now handles FormData correctly
      const responseData = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CREATE_IMAGE, "", null),
        {
          method: "POST",
          body: formData,
        },
      );

      // Ensure we have a valid response - image uploads should always return data
      if (!responseData) {
        throw new Error("Image upload failed: Empty response from server");
      }

      return responseData as ImageUploadResponse;
    } catch (error) {
      throw error;
    }
  };

  // Clears the salon's saved logo (sets business_locations.logo_s3_uri = NULL).
  // Logo writes live in the SSG service alongside /create-image, so removal has
  // its own SSG endpoint rather than going through PUT /business-locations.
  const removeSalonLogo = async (
    businessLocationId: string,
  ): Promise<{ message?: string }> => {
    const responseData = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.REMOVE_SALON_LOGO, "", null),
      {
        method: "POST",
        body: JSON.stringify({ business_location_id: businessLocationId }),
      },
    );
    return (responseData ?? {}) as { message?: string };
  };

  const getImages = async (
    request: GetImagesRequest,
  ): Promise<GetImagesResponse> => {
    try {
      // SSG GET /images/{salonId} — defaults match product spec (marketing library only)
      const includeThumbnails =
        request.include_thumbnails !== undefined
          ? request.include_thumbnails
          : true;
      const onlyThumbnails =
        request.only_thumbnails !== undefined ? request.only_thumbnails : false;

      const queryParams: string[] = [
        `include_thumbnails=${includeThumbnails}`,
        `only_thumbnails=${onlyThumbnails}`,
      ];
      if (request.prefix !== undefined && request.prefix !== "") {
        queryParams.push(`prefix=${encodeURIComponent(request.prefix)}`);
      }

      const queryString = queryParams.join("&");

      const responseData = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GET_SALON_IMAGES, queryString, {
          salonId: request.salonId,
        }),
        {
          method: "GET",
        },
      );

      return responseData as GetImagesResponse;
    } catch (error) {
      throw error;
    }
  };

  const deleteImage = async (
    request: DeleteImageRequest,
  ): Promise<DeleteImageResponse> => {
    try {
      // Build query parameters
      const queryParams: string[] = [];
      if (request.delete_thumbnail !== undefined) {
        queryParams.push(
          `delete_thumbnail=${request.delete_thumbnail.toString()}`,
        );
      }

      const queryString = queryParams.join("&");

      const responseData = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.DELETE_IMAGE, queryString, {
          salonId: request.salonId,
          filename: encodeURIComponent(request.filename),
        }),
        {
          method: "DELETE",
        },
      );

      return responseData as DeleteImageResponse;
    } catch (error) {
      console.error("Error deleting image:", error);
      throw error;
    }
  };

  // ==========================================
  // Salon Documents (SSG)
  // ==========================================

  const listSalonDocuments = async (
    salonId: string,
    clientId?: string,
  ): Promise<DocumentListResponse> => {
    const queryParams = clientId
      ? `clientId=${encodeURIComponent(clientId)}`
      : "";
    const responseData = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.LIST_SALON_DOCUMENTS, queryParams, {
        salonId,
      }),
      { method: "GET" },
    );
    return responseData as DocumentListResponse;
  };

  const uploadSalonDocument = async (
    salonId: string,
    documentKey: string,
    file: File | Blob,
    contentType: string,
    metadata?: Record<string, unknown>,
    clientId?: string,
    displayName?: string,
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append("document_key", documentKey);
    formData.append("content_type", contentType);

    // Filename extension must match an allowed S3 type (not `.bin`).
    const fileName = salonDocumentUploadFilename(
      documentKey,
      file,
      contentType,
      clientId != null,
    );
    formData.append("file", file, fileName);

    if (metadata) {
      formData.append("metadata", JSON.stringify(metadata));
    }
    if (clientId) {
      formData.append("clientId", clientId);
    }
    if (displayName !== undefined) {
      formData.append("displayName", displayName);
      formData.append("display_name", displayName);
    }

    const responseData = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.UPLOAD_SALON_DOCUMENT, "", {
        salonId,
      }),
      { method: "POST", body: formData },
    );
    return responseData as DocumentUploadResponse;
  };

  const getSalonDocumentDetail = async (
    salonId: string,
    documentKey: string,
    clientId?: string,
  ): Promise<DocumentDetailResponse> => {
    const queryParams = clientId
      ? `clientId=${encodeURIComponent(clientId)}`
      : "";
    const responseData = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.GET_SALON_DOCUMENT, queryParams, {
        salonId,
        documentKey,
      }),
      { method: "GET" },
    );
    return responseData as DocumentDetailResponse;
  };

  const deleteSalonDocument = async (
    salonId: string,
    documentKey: string,
    clientId?: string,
  ): Promise<DocumentDeleteResponse | null> => {
    const queryParams = clientId
      ? `clientId=${encodeURIComponent(clientId)}`
      : "";
    const responseData = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.DELETE_SALON_DOCUMENT, queryParams, {
        salonId,
        documentKey: encodeURIComponent(documentKey),
      }),
      { method: "DELETE" },
    );
    return (responseData ?? null) as DocumentDeleteResponse | null;
  };

  const revertSalonDocument = async (
    salonId: string,
    documentKey: string,
    versionUuid: string,
    clientId?: string,
  ): Promise<DocumentRevertResponse> => {
    const queryParams = clientId
      ? `clientId=${encodeURIComponent(clientId)}`
      : "";
    const responseData = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.REVERT_SALON_DOCUMENT, queryParams, {
        salonId,
        documentKey,
      }),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ versionUuid }),
      },
    );
    return responseData as DocumentRevertResponse;
  };

  const downloadSalonDocument = async (
    salonId: string,
    documentKey: string,
    clientId: string,
  ): Promise<SalonDocumentPhidDownload> => {
    const token = await getIdToken();
    if (!token) {
      throw new Error("No authentication token available");
    }
    const encClient = encodeURIComponent(clientId);
    const queryParams = `clientId=${encClient}&client_id=${encClient}`;
    const url = getApiUrl(
      API_CONFIG.ENDPOINTS.DOWNLOAD_SALON_DOCUMENT,
      queryParams,
      { salonId, documentKey: encodeURIComponent(documentKey) },
    );
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/octet-stream",
        ...actingLocationHeaders(),
      },
    });

    if (!response.ok) {
      let message = "Failed to download document";
      const ct = (response.headers.get("content-type") || "").toLowerCase();
      try {
        if (ct.includes("application/json")) {
          const body = (await response.json()) as {
            message?: string;
            error?: string;
          };
          message = body.message || body.error || message;
        }
      } catch {
        /* keep default message */
      }
      const error = new Error(message);
      (error as any).status = response.status;
      throw error;
    }

    const cd = response.headers.get("content-disposition");
    const serverFilename = parseContentDispositionFilename(cd);
    const responseContentType =
      response.headers.get("x-content-type") ||
      response.headers.get("content-type") ||
      undefined;
    const blob = await response.blob();
    return { blob, serverFilename, responseContentType };
  };

  // Get Rollfi payroll period
  const getRollfiPayrollPeriod = useCallback(
    async (companyId: string): Promise<GetPayrollPeriodResponse> => {
      try {
        const requestData: GetPayrollPeriodRequest = {
          companyId,
          workerType: "W2",
        };
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLLFI_PAYROLL_PERIOD, "", null),
          {
            method: "POST",
            body: JSON.stringify(requestData),
          },
        );
        return responseJson as GetPayrollPeriodResponse;
      } catch (error) {
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const createRollfiBusiness = useCallback(
    async (
      businessData: CreateBusinessRequest,
    ): Promise<CreateBusinessResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLLFI_CREATE_BUSINESS, "", null),
          {
            method: "POST",
            body: JSON.stringify(businessData),
          },
        );
        return responseJson as CreateBusinessResponse;
      } catch (error) {
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const getRollfiActiveUsers = useCallback(
    async (request: GetActiveUsersRequest): Promise<GetActiveUsersResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLLFI_ACTIVE_USERS, "", null),
          {
            method: "POST",
            body: JSON.stringify(request),
          },
        );
        return responseJson as GetActiveUsersResponse;
      } catch (error) {
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const syncRollfiPayroll = useCallback(
    async (payrollData: SyncPayrollRequest): Promise<SyncPayrollResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLLFI_SYNC_PAYROLL, "", null),
          {
            method: "POST",
            body: JSON.stringify(payrollData),
          },
        );
        return responseJson as SyncPayrollResponse;
      } catch (error) {
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const initiateSso = useCallback(async (): Promise<InitiateSsoResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.ROLLFI_SSO_INITIATE, "", null),
        {
          method: "POST",
          body: JSON.stringify({}),
        },
      );
      return responseJson as InitiateSsoResponse;
    } catch (error) {
      console.error("Error initiating SSO:", error);
      throw error;
    }
  }, [fetchWithAuth]);

  const prepPayrollSync = useCallback(
    async (
      request: PrepPayrollSyncRequest,
    ): Promise<PrepPayrollSyncResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.PREP_PAYROLL_SYNC, "", null),
          {
            method: "POST",
            body: JSON.stringify(request),
          },
        );
        return responseJson as PrepPayrollSyncResponse;
      } catch (error) {
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get time tracking status for an employee
  const getTimeTrackingStatus = useCallback(
    async (
      userId: string,
      companyId: string,
    ): Promise<TimeTrackingStatusResponse> => {
      try {
        const responseJson = (await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_EMPLOYEE, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "getEmployeeTimeTrackingStatus",
              userId,
              companyId,
            }),
          },
        )) as TimeTrackingStatusRawResponse;

        // Transform RollFi response to normalized frontend shape
        const status = responseJson.employeeTimeStatus;
        return {
          isClockedIn: status?.clockedIn ?? false,
          isOnBreak: status?.onBreak ?? false,
          timeEntryId: status?.timeEntryId,
          clockInTime: status?.clockInTime,
          breakStartTime: status?.breakStartTime,
        };
      } catch (error) {
        console.error("Error getting time tracking status:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // ==========================================
  // ONBOARDING API METHODS
  // ==========================================

  // Get onboarding progress
  const getOnboardingProgress = useCallback(
    async (salonId?: string): Promise<OnboardingProgressResponse | null> => {
      try {
        const queryParams = salonId ? `salonId=${salonId}` : "";
        const responseJson = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.ONBOARDING_PROGRESS,
            queryParams,
            null,
          ),
        );
        // Backend returns 200 with {} when no onboarding row for salon/staff
        if (isEmptyObject(responseJson)) {
          recordApiEmptyResponseWarning(
            "GET /onboarding",
            "Empty object (no onboarding row yet)",
          );
          return null;
        }
        return responseJson as OnboardingProgressResponse;
      } catch (error) {
        // Don't log 404 errors for new users who don't have onboarding progress yet
        // Check the error message to determine if this is a new user issue
        const status = (error as any)?.status;
        const errorMessage = (error as Error)?.message || "";
        const isNewUserError =
          status === 404 &&
          (errorMessage.includes("Onboarding row not found") || !salonId); // Also check if no salonId was provided

        if (isNewUserError) {
          // Silently fail for new users - they don't have onboarding progress yet
        } else {
          // Log all other errors (including 404s for returning users)
          console.error("Error getting onboarding progress:", error);
        }
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get employee pay period time card detail (new API - replaces getEmployeeTimeEntriesV2)
  const getEmployeePayPeriodTimeCard = useCallback(
    async (
      userId: string,
      companyId: string,
      payPeriodId: string,
    ): Promise<GetEmployeePayPeriodTimeCardResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_EMPLOYEE, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "getEmployeePayPeriodTimeCard",
              userId,
              companyId,
              payPeriodId,
            }),
          },
        );
        return responseJson as GetEmployeePayPeriodTimeCardResponse;
      } catch (error) {
        console.error("Error getting employee pay period time card:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Update onboarding progress
  const updateOnboardingProgress = useCallback(
    async (
      data: UpdateOnboardingProgressRequest,
      salonId?: string,
    ): Promise<OnboardingProgressResponse> => {
      try {
        const queryParams = salonId ? `salonId=${salonId}` : "";
        const responseJson = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.ONBOARDING_PROGRESS,
            queryParams,
            null,
          ),
          {
            method: "PUT",
            body: JSON.stringify(data),
          },
        );
        return responseJson as OnboardingProgressResponse;
      } catch (error) {
        console.error("Error updating onboarding progress:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Perform time tracking action (clock in/out, start/end break)
  const performTimeTrackingAction = useCallback(
    async (
      request: TimeTrackingActionRequest,
    ): Promise<TimeTrackingActionResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify(request),
          },
        );
        return responseJson as TimeTrackingActionResponse;
      } catch (error) {
        console.error("Error performing time tracking action:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get pay period time cards (new API - replaces getPayPeriodTimeSheetV2)
  // Returns time cards filtered by role (all for Owner/Manager, own for Employee)
  const getPayPeriodTimeCards = useCallback(
    async (
      companyId: string,
      payPeriodId: string,
    ): Promise<GetPayPeriodTimeCardsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_EMPLOYEE, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "getPayPeriodTimeCards",
              companyId,
              payPeriodId,
            }),
          },
        );
        return responseJson as GetPayPeriodTimeCardsResponse;
      } catch (error) {
        console.error("Error getting pay period time cards:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Approve or reset time card approvals
  const updateTimeCardApprovals = useCallback(
    async (
      companyId: string,
      payPeriodId: string,
      timeCardIds: string[],
      isApproved: boolean,
    ): Promise<UpdateTimeCardApprovalsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "updateTimeCardApprovals",
              companyId,
              payPeriodId,
              timeCardIds,
              isApproved,
            }),
          },
        );
        return responseJson as UpdateTimeCardApprovalsResponse;
      } catch (error) {
        console.error("Error updating time card approvals:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Delete a time entry
  const deleteTimeEntry = useCallback(
    async (timeEntryId: string): Promise<DeleteTimeEntryResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "deleteTimeEntry",
              timeEntryId,
            }),
          },
        );
        return responseJson as DeleteTimeEntryResponse;
      } catch (error) {
        console.error("Error deleting time entry:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Upsert a time entry (insert or update)
  const upsertTimeEntry = useCallback(
    async (
      userId: string,
      companyId: string,
      timeEntry: UpsertTimeEntryData,
    ): Promise<UpsertTimeEntryResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "upsertTimeEntry",
              userId,
              companyId,
              timeEntry,
            }),
          },
        );
        return responseJson as UpsertTimeEntryResponse;
      } catch (error) {
        console.error("Error upserting time entry:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Sync approved time cards to payroll
  const syncTimeCardsToPayroll = useCallback(
    async (
      companyId: string,
      payPeriodId: string,
      timeCardIds: string[],
    ): Promise<SyncTimeCardsToPayrollResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "syncTimeCardsToPayroll",
              companyId,
              payPeriodId,
              timeCardIds,
            }),
          },
        );
        return responseJson as SyncTimeCardsToPayrollResponse;
      } catch (error) {
        console.error("Error syncing time cards to payroll:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // ==========================================
  // TIME TRACKING POLICY / ASSIGNMENT METHODS
  //
  // The six methods below (getTimeTrackingPolicy, addTimeTrackingPolicy,
  // updateTimeTrackingPolicy, deleteTimeTrackingPolicy, addPolicyAssignments,
  // removePolicyAssignments) are exposed on AppContext but currently have no
  // consuming UI components. They are pre-work for an admin-only policy and
  // assignment management screen. When that screen lands, wire it here;
  // until then these methods stay available for direct use.
  // ==========================================

  // Get time tracking policy for a company
  const getTimeTrackingPolicy = useCallback(
    async (companyId: string): Promise<GetTimeTrackingPolicyResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_EMPLOYEE, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "getTimeTrackingPolicy",
              companyId,
            }),
          },
        );
        return responseJson as GetTimeTrackingPolicyResponse;
      } catch (error) {
        console.error("Error getting time tracking policy:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Add a time tracking policy
  const addTimeTrackingPolicy = useCallback(
    async (
      companyId: string,
      dailyOvertimeThreshold?: number,
      weeklyOvertimeThreshold?: number,
    ): Promise<AddTimeTrackingPolicyResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "addTimeTrackingPolicy",
              companyId,
              ...(dailyOvertimeThreshold !== undefined && {
                dailyOvertimeThreshold,
              }),
              ...(weeklyOvertimeThreshold !== undefined && {
                weeklyOvertimeThreshold,
              }),
            }),
          },
        );
        return responseJson as AddTimeTrackingPolicyResponse;
      } catch (error) {
        console.error("Error adding time tracking policy:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Update a time tracking policy
  const updateTimeTrackingPolicy = useCallback(
    async (
      companyId: string,
      timeTrackingPolicyId: string,
      dailyOvertimeThreshold?: number,
      weeklyOvertimeThreshold?: number,
    ): Promise<UpdateTimeTrackingPolicyResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "updateTimeTrackingPolicy",
              companyId,
              timeTrackingPolicyId,
              ...(dailyOvertimeThreshold !== undefined && {
                dailyOvertimeThreshold,
              }),
              ...(weeklyOvertimeThreshold !== undefined && {
                weeklyOvertimeThreshold,
              }),
            }),
          },
        );
        return responseJson as UpdateTimeTrackingPolicyResponse;
      } catch (error) {
        console.error("Error updating time tracking policy:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Delete a time tracking policy
  const deleteTimeTrackingPolicy = useCallback(
    async (
      companyId: string,
      timeTrackingPolicyId: string,
    ): Promise<DeleteTimeTrackingPolicyResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "deleteTimeTrackingPolicy",
              companyId,
              timeTrackingPolicyId,
            }),
          },
        );
        return responseJson as DeleteTimeTrackingPolicyResponse;
      } catch (error) {
        console.error("Error deleting time tracking policy:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Add employees to a time tracking policy
  const addPolicyAssignments = useCallback(
    async (
      companyId: string,
      timeTrackingPolicyId: string,
      userIds: string[],
    ): Promise<PolicyAssignmentsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "addTimeTrackingPolicyAssignments",
              companyId,
              timeTrackingPolicyId,
              userIds,
            }),
          },
        );
        return responseJson as PolicyAssignmentsResponse;
      } catch (error) {
        console.error("Error adding policy assignments:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Remove employees from a time tracking policy
  const removePolicyAssignments = useCallback(
    async (
      companyId: string,
      timeTrackingPolicyId: string,
      userIds: string[],
    ): Promise<PolicyAssignmentsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.TIME_TRACKING_ACTION, "", null),
          {
            method: "POST",
            body: JSON.stringify({
              method: "removeTimeTrackingPolicyAssignments",
              companyId,
              timeTrackingPolicyId,
              userIds,
            }),
          },
        );
        return responseJson as PolicyAssignmentsResponse;
      } catch (error) {
        console.error("Error removing policy assignments:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // ==========================================
  // PRODUCT STOCK ADJUSTMENT METHODS
  // ==========================================

  // Create product stock adjustment
  const createProductStockAdjustment = useCallback(
    async (
      productId: string,
      adjustmentData: CreateProductStockAdjustmentRequest,
    ): Promise<ProductStockAdjustment> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.PRODUCT_STOCK_ADJUSTMENTS, "", {
            productId,
          }),
          {
            method: "POST",
            body: JSON.stringify({ adjustments: [adjustmentData] }),
          },
        );
        // API returns array, get first item
        const adjustments = responseJson as ProductStockAdjustment[];
        return adjustments[0];
      } catch (error) {
        console.error("Error creating product stock adjustment:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get all saved onboarding answers (single API call)
  const getOnboardingQuestions = useCallback(
    async (
      salonId?: string,
      questionKeys?: string[],
    ): Promise<GetOnboardingQuestionsResponse> => {
      try {
        const queryParts: string[] = [];
        if (salonId) {
          queryParts.push(`salonId=${salonId}`);
        }
        if (questionKeys && questionKeys.length > 0) {
          queryParts.push(`questionKeys=${questionKeys.join(",")}`);
        }
        const queryParams = queryParts.join("&");
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ONBOARDING_ANSWERS, queryParams, null),
        );
        return responseJson as GetOnboardingQuestionsResponse;
      } catch (error) {
        console.error("Error getting onboarding answers:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Upsert an onboarding answer
  const upsertOnboardingAnswer = useCallback(
    async (
      data: UpsertOnboardingAnswerRequest,
      salonId?: string,
    ): Promise<UpsertOnboardingAnswerResponse> => {
      try {
        const queryParams = salonId ? `salonId=${salonId}` : "";
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ONBOARDING_ANSWERS, queryParams, null),
          {
            method: "PUT",
            body: JSON.stringify(data),
          },
        );
        return responseJson as UpsertOnboardingAnswerResponse;
      } catch (error) {
        console.error("Error upserting onboarding answer:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Update product stock adjustment
  const updateProductStockAdjustment = useCallback(
    async (
      adjustmentId: string,
      updates: UpdateProductStockAdjustmentRequest,
    ): Promise<ProductStockAdjustment> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.PRODUCT_STOCK_ADJUSTMENT_BY_ID, "", {
            adjustmentId,
          }),
          {
            method: "PUT",
            body: JSON.stringify(updates),
          },
        );
        return responseJson as ProductStockAdjustment;
      } catch (error) {
        console.error("Error updating product stock adjustment:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Delete product stock adjustment
  const deleteProductStockAdjustment = useCallback(
    async (adjustmentId: string): Promise<void> => {
      try {
        await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.PRODUCT_STOCK_ADJUSTMENT_BY_ID, "", {
            adjustmentId,
          }),
          {
            method: "DELETE",
          },
        );
      } catch (error) {
        console.error("Error deleting product stock adjustment:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Commission methods
  const getStaffCommissions = useCallback(
    async (staffId?: string): Promise<GetCommissionsResponse> => {
      try {
        const url = staffId
          ? getApiUrl(API_CONFIG.ENDPOINTS.STAFF_COMMISSIONS, "", { staffId })
          : getApiUrl(API_CONFIG.ENDPOINTS.COMMISSIONS, "", null);
        const responseJson = await fetchWithAuth(url);
        return responseJson as GetCommissionsResponse;
      } catch (error) {
        console.error("Error fetching staff commissions:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const createCommissions = useCallback(
    async (
      request: CreateCommissionsRequest,
    ): Promise<CreateCommissionsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.COMMISSIONS, "", null),
          {
            method: "POST",
            body: JSON.stringify(request),
          },
        );
        return responseJson as CreateCommissionsResponse;
      } catch (error) {
        console.error("Error creating commissions:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const deleteCommission = useCallback(
    async (commissionId: string): Promise<DeleteCommissionResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.COMMISSION_BY_ID, "", {
            commissionId,
          }),
          {
            method: "DELETE",
          },
        );
        return responseJson as DeleteCommissionResponse;
      } catch (error) {
        console.error("Error deleting commission:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // ==========================================
  // Staff Tier methods
  // ==========================================
  const getStaffTiers = useCallback(
    async (salonId: string): Promise<GetStaffTiersResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIERS, "", { salonId }),
        );
        return responseJson as GetStaffTiersResponse;
      } catch (error) {
        console.error("Error fetching staff tiers:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const createStaffTier = useCallback(
    async (
      salonId: string,
      request: CreateStaffTierRequest,
    ): Promise<StaffTier> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIERS, "", { salonId }),
          { method: "POST", body: JSON.stringify(request) },
        );
        return responseJson as StaffTier;
      } catch (error) {
        console.error("Error creating staff tier:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const updateStaffTier = useCallback(
    async (
      tierId: string,
      request: UpdateStaffTierRequest,
    ): Promise<StaffTier> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_BY_ID, "", { tierId }),
          { method: "PUT", body: JSON.stringify(request) },
        );
        return responseJson as StaffTier;
      } catch (error) {
        console.error("Error updating staff tier:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const reorderStaffTiers = useCallback(
    async (
      salonId: string,
      request: ReorderStaffTiersRequest,
    ): Promise<GetStaffTiersResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIERS_ORDER, "", { salonId }),
          { method: "PUT", body: JSON.stringify(request) },
        );
        return responseJson as GetStaffTiersResponse;
      } catch (error) {
        console.error("Error reordering staff tiers:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Persist the drag-reordered staff display order (Staff Settings "Edit
  // Order"). The backend re-sorts GET /business-locations/{id}/staff by sort_order, so the
  // caller refreshes the roster (SalonContext) after this resolves.
  const reorderStaff = useCallback(
    async (
      salonId: string,
      request: ReorderStaffRequest,
    ): Promise<{ message: string }> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SALON_STAFF_ORDER, "", { salonId }),
          { method: "PUT", body: JSON.stringify(request) },
        );
        return responseJson as { message: string };
      } catch (error) {
        console.error("Error reordering staff:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const deleteStaffTier = useCallback(
    async (tierId: string): Promise<{ message: string; id: string }> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_BY_ID, "", { tierId }),
          { method: "DELETE" },
        );
        return responseJson as { message: string; id: string };
      } catch (error) {
        console.error("Error deleting staff tier:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const assignStaffTier = useCallback(
    async (
      salonId: string,
      staffId: string,
      request: AssignStaffTierRequest,
    ): Promise<{ staff_id: string; staff_tier_id: string | null }> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_ASSIGN, "", {
            salonId,
            staffId,
          }),
          { method: "PUT", body: JSON.stringify(request) },
        );
        return responseJson as {
          staff_id: string;
          staff_tier_id: string | null;
        };
      } catch (error) {
        console.error("Error assigning staff tier:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const getTierCommissionDefault = useCallback(
    async (tierId: string): Promise<GetCommissionDefaultResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_COMMISSION_DEFAULTS, "", {
            tierId,
          }),
        );
        return responseJson as GetCommissionDefaultResponse;
      } catch (error) {
        console.error("Error fetching tier commission default:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const upsertTierCommissionDefault = useCallback(
    async (
      tierId: string,
      request: UpsertCommissionDefaultRequest,
    ): Promise<StaffTierCommissionDefault> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_COMMISSION_DEFAULTS, "", {
            tierId,
          }),
          { method: "PUT", body: JSON.stringify(request) },
        );
        return responseJson as StaffTierCommissionDefault;
      } catch (error) {
        console.error("Error saving tier commission default:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const deleteTierCommissionDefault = useCallback(
    async (tierId: string): Promise<{ message: string }> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_COMMISSION_DEFAULTS, "", {
            tierId,
          }),
          { method: "DELETE" },
        );
        return responseJson as { message: string };
      } catch (error) {
        console.error("Error deleting tier commission default:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const applyTierCommissionDefaults = useCallback(
    async (
      tierId: string,
      request: ApplyCommissionDefaultsRequest,
    ): Promise<ApplyDefaultsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.STAFF_TIER_APPLY_COMMISSION_DEFAULTS,
            "",
            { tierId },
          ),
          { method: "POST", body: JSON.stringify(request) },
        );
        return responseJson as ApplyDefaultsResponse;
      } catch (error) {
        console.error("Error applying tier commission defaults:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const getTierServiceDefaults = useCallback(
    async (tierId: string): Promise<GetServiceDefaultsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_SERVICE_DEFAULTS, "", {
            tierId,
          }),
        );
        return responseJson as GetServiceDefaultsResponse;
      } catch (error) {
        console.error("Error fetching tier service defaults:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const upsertTierServiceDefaults = useCallback(
    async (
      tierId: string,
      request: UpsertServiceDefaultsRequest,
    ): Promise<GetServiceDefaultsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_SERVICE_DEFAULTS, "", {
            tierId,
          }),
          { method: "PUT", body: JSON.stringify(request) },
        );
        return responseJson as GetServiceDefaultsResponse;
      } catch (error) {
        console.error("Error saving tier service defaults:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const deleteTierServiceDefault = useCallback(
    async (
      tierId: string,
      salonServiceId: string,
    ): Promise<{ message: string }> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.STAFF_TIER_SERVICE_DEFAULT_BY_SERVICE,
            "",
            { tierId, salonServiceId },
          ),
          { method: "DELETE" },
        );
        return responseJson as { message: string };
      } catch (error) {
        console.error("Error deleting tier service default:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const applyTierServiceDefaults = useCallback(
    async (
      tierId: string,
      request: ApplyServiceDefaultsRequest,
    ): Promise<ApplyDefaultsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.STAFF_TIER_APPLY_SERVICE_DEFAULTS,
            "",
            {
              tierId,
            },
          ),
          { method: "POST", body: JSON.stringify(request) },
        );
        return responseJson as ApplyDefaultsResponse;
      } catch (error) {
        console.error("Error applying tier service defaults:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const reassignAndDeleteTier = useCallback(
    async (
      tierId: string,
      request: ReassignAndDeleteRequest,
    ): Promise<ReassignAndDeleteResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_TIER_REASSIGN_AND_DELETE, "", {
            tierId,
          }),
          { method: "POST", body: JSON.stringify(request) },
        );
        return responseJson as ReassignAndDeleteResponse;
      } catch (error) {
        console.error("Error reassigning staff and deleting tier:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get feature access (subscription tier, features, onboarding state)
  const getAccess = useCallback(async (): Promise<FeatureAccessResponse> => {
    try {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.ACCESS, "", null),
        {
          method: "GET",
        },
      );
      return responseJson as FeatureAccessResponse;
    } catch (error) {
      console.error("Error getting feature access:", error);
      throw error;
    }
  }, [fetchWithAuth]);

  // Multi-location (PR A / #399): persist which location a multi-location
  // staff member is viewing. The backend authorizes (403 unless the caller has
  // an active assignment at that salon) and sets staff_users.last_used_salon_id
  // so the next /access auto-lands there and reads are scoped to it.
  const patchStaffUsersMe = useCallback(
    async (lastUsedSalonId: number): Promise<any> => {
      try {
        return await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.STAFF_USERS_ME, "", null),
          {
            method: "PATCH",
            body: JSON.stringify({ last_used_salon_id: lastUsedSalonId }),
          },
        );
      } catch (error) {
        console.error("Error updating last used salon:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get availability for rebooking
  const getAvailability = useCallback(
    async (params: AvailabilityRequest): Promise<AvailabilityResponse> => {
      try {
        // Build query parameters
        const queryParts: string[] = [
          `start_date=${params.start_date}`,
          `end_date=${params.end_date}`,
          `services=${params.services}`,
          `timezone=${encodeURIComponent(params.timezone)}`,
          `base_duration_mins=${params.base_duration_mins}`,
          `processing_time_mins=${params.processing_time_mins}`,
          `finishing_time_mins=${params.finishing_time_mins}`,
          `buffer_time_mins=${params.buffer_time_mins}`,
        ];

        if (params.staff_id) {
          queryParts.push(`staff_id=${params.staff_id}`);
        }

        if (params.service_adjustments) {
          queryParts.push(`service_adjustments=${params.service_adjustments}`);
        }

        const queryString = queryParts.join("&");

        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.AVAILABILITY, queryString, null),
          {
            method: "GET",
          },
        );

        return responseJson as AvailabilityResponse;
      } catch (error) {
        console.error("Error getting availability:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Get role permissions for a salon (workflow-based)
  const getRolePermissions = useCallback(
    async (salonId: string): Promise<GetRolePermissionsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLE_PERMISSIONS, "", { salonId }),
          {
            method: "GET",
          },
        );
        return responseJson as GetRolePermissionsResponse;
      } catch (error) {
        console.error("Error getting role permissions:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Update all permissions for a single role (PUT to same path as GET; one role per request)
  const batchUpdateRolePermissions = useCallback(
    async (
      salonId: string,
      data: BatchUpdatePermissionsRequest,
    ): Promise<BatchUpdatePermissionsResponse> => {
      if (
        !data?.role ||
        typeof data.permissions !== "object" ||
        data.permissions === null
      ) {
        throw new Error(
          "Role permissions update requires role and permissions object",
        );
      }
      const body = JSON.stringify({
        role: data.role,
        permissions: data.permissions,
      });
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLE_PERMISSIONS, "", { salonId }),
          {
            method: "PUT",
            body,
          },
        );
        return responseJson as BatchUpdatePermissionsResponse;
      } catch (error) {
        console.error("Error updating role permissions:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // Reset all permissions for a role to defaults
  const resetRolePermissions = useCallback(
    async (salonId: string, role: Role): Promise<ResetPermissionsResponse> => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.ROLE_PERMISSIONS_RESET, "", {
            salonId,
            role,
          }),
          {
            method: "DELETE",
          },
        );
        return responseJson as ResetPermissionsResponse;
      } catch (error) {
        console.error("Error resetting role permissions:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  // ==========================================
  // Service Packages (prepaid bundles)
  // ==========================================

  const getServicePackages = useCallback(
    async (salonId: string): Promise<ServicePackage[]> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_PACKAGES, "", { salonId }),
      );
      return (responseJson as ServicePackage[]) ?? [];
    },
    [fetchWithAuth],
  );

  const createServicePackage = useCallback(
    async (
      salonId: string,
      body: CreateServicePackageRequest,
    ): Promise<ServicePackage> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_PACKAGES, "", { salonId }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const updateServicePackage = useCallback(
    async (
      salonId: string,
      packageId: string,
      body: UpdateServicePackageRequest,
    ): Promise<ServicePackage> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_PACKAGE_BY_ID, "", {
          salonId,
          packageId,
        }),
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const deleteServicePackage = useCallback(
    async (
      salonId: string,
      packageId: string,
    ): Promise<ArchiveServicePackageResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SERVICE_PACKAGE_BY_ID, "", {
          salonId,
          packageId,
        }),
        {
          method: "DELETE",
        },
      );
    },
    [fetchWithAuth],
  );

  const getClientServicePackages = useCallback(
    async (
      salonId: string,
      clientId: string,
      // Client-profile management shows depleted/expired/revoked packages too;
      // customer-facing callers (checkout, transaction detail) omit this and
      // stay active-only.
      includeInactive = false,
    ): Promise<ClientServicePackage[]> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.CLIENT_SERVICE_PACKAGES,
          includeInactive ? "include_inactive=true" : "",
          {
            salonId,
            clientId,
          },
        ),
      );
      return (responseJson as ClientServicePackage[]) ?? [];
    },
    [fetchWithAuth],
  );

  const getClientActivity = useCallback(
    async (
      salonId: string,
      clientId: string,
      cursor?: string | null,
      limit?: number,
    ): Promise<ClientActivityResponse> => {
      const query: string[] = [];
      if (cursor) query.push(`cursor=${encodeURIComponent(cursor)}`);
      if (limit) query.push(`limit=${limit}`);
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_ACTIVITY, query.join("&"), {
          salonId,
          clientId,
        }),
      );
      const data = responseJson as ClientActivityResponse | null;
      return {
        events: data?.events ?? [],
        next_cursor: data?.next_cursor ?? null,
      };
    },
    [fetchWithAuth],
  );

  const getClientPackageAdjustments = useCallback(
    async (
      salonId: string,
      cspId: string,
    ): Promise<ClientPackageAdjustment[]> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_PACKAGE_ADJUSTMENTS, "", {
          salonId,
          cspId,
        }),
      );
      // The core GET endpoint returns a bare array; tolerate a
      // { adjustments: [] } envelope too so either backend shape works.
      const data = responseJson as
        | ClientPackageAdjustment[]
        | { adjustments?: ClientPackageAdjustment[] }
        | null;
      return Array.isArray(data) ? data : (data?.adjustments ?? []);
    },
    [fetchWithAuth],
  );

  const addClientServicePackage = useCallback(
    async (
      salonId: string,
      clientId: string,
      body: AddPackageToClientRequest,
    ): Promise<AddPackageToClientResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_SERVICE_PACKAGES, "", {
          salonId,
          clientId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const adjustClientPackageBalance = useCallback(
    async (
      salonId: string,
      cspId: string,
      salonServiceId: string,
      body: AdjustBankRequest,
    ): Promise<AdjustBankResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.ADJUST_CLIENT_PACKAGE_BALANCE, "", {
          salonId,
          cspId,
          salonServiceId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const editClientPackageExpiry = useCallback(
    async (
      salonId: string,
      cspId: string,
      body: EditExpiryRequest,
    ): Promise<EditExpiryResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.EDIT_CLIENT_PACKAGE_EXPIRY, "", {
          salonId,
          cspId,
        }),
        {
          method: "PATCH",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const revokeClientPackage = useCallback(
    async (
      salonId: string,
      cspId: string,
      body: RevokePackageRequest,
    ): Promise<RevokePackageResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.REVOKE_CLIENT_PACKAGE, "", {
          salonId,
          cspId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const sellServicePackage = useCallback(
    async (
      salonId: string,
      body: SellServicePackageRequest,
    ): Promise<SellServicePackageResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SELL_SERVICE_PACKAGE, "", { salonId }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const redeemPackage = useCallback(
    async (
      salonId: string,
      body: RedeemPackageRequest,
    ): Promise<RedeemPackageResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.REDEEM_SERVICE_PACKAGE, "", { salonId }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  // === Loyalty (points-as-currency MVP) ====================================
  // Backend PR2 (location config) + PR5 (client account) endpoints. Not
  // deployed yet — surfaces are feature-gated and tolerate failures.

  const getLoyaltySettings = useCallback(
    async (
      businessLocationId: string,
    ): Promise<Partial<LoyaltySettings> | null> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_SETTINGS, "", {
          businessLocationId,
        }),
      );
    },
    [fetchWithAuth],
  );

  const getLoyaltyLocationSummary = useCallback(
    async (
      businessLocationId: string,
      params?: { start_date?: string; end_date?: string },
    ): Promise<LoyaltyLocationSummaryResponse | null> => {
      const query = new URLSearchParams();
      if (params?.start_date) query.set("start_date", params.start_date);
      if (params?.end_date) query.set("end_date", params.end_date);
      const qs = query.toString();
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_SUMMARY, qs, {
          businessLocationId,
        }),
      );
    },
    [fetchWithAuth],
  );

  const updateLoyaltySettings = useCallback(
    async (
      businessLocationId: string,
      data: UpdateLoyaltySettingsRequest,
    ): Promise<LoyaltySettings> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_SETTINGS, "", {
          businessLocationId,
        }),
        { method: "PUT", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const getLoyaltyEarnRules = useCallback(
    async (businessLocationId: string): Promise<LoyaltyEarnRule[]> => {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_EARN_RULES, "", {
          businessLocationId,
        }),
      );
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.earn_rules)) return response.earn_rules;
      return [];
    },
    [fetchWithAuth],
  );

  const createLoyaltyEarnRule = useCallback(
    async (
      businessLocationId: string,
      data: CreateLoyaltyEarnRuleRequest,
    ): Promise<LoyaltyEarnRule> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_EARN_RULES, "", {
          businessLocationId,
        }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const updateLoyaltyEarnRule = useCallback(
    async (
      businessLocationId: string,
      ruleId: string,
      data: UpdateLoyaltyEarnRuleRequest,
    ): Promise<LoyaltyEarnRule> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_EARN_RULE_BY_ID, "", {
          businessLocationId,
          ruleId,
        }),
        { method: "PUT", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const deleteLoyaltyEarnRule = useCallback(
    async (businessLocationId: string, ruleId: string): Promise<any> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_EARN_RULE_BY_ID, "", {
          businessLocationId,
          ruleId,
        }),
        { method: "DELETE" },
      );
    },
    [fetchWithAuth],
  );

  const getLoyaltyRewards = useCallback(
    async (businessLocationId: string): Promise<LoyaltyReward[]> => {
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_REWARDS, "", {
          businessLocationId,
        }),
      );
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.rewards)) return response.rewards;
      return [];
    },
    [fetchWithAuth],
  );

  const createLoyaltyReward = useCallback(
    async (
      businessLocationId: string,
      data: CreateLoyaltyRewardRequest,
    ): Promise<LoyaltyReward> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_REWARDS, "", {
          businessLocationId,
        }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const updateLoyaltyReward = useCallback(
    async (
      businessLocationId: string,
      rewardId: string,
      data: UpdateLoyaltyRewardRequest,
    ): Promise<LoyaltyReward> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_REWARD_BY_ID, "", {
          businessLocationId,
          rewardId,
        }),
        { method: "PUT", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const deleteLoyaltyReward = useCallback(
    async (businessLocationId: string, rewardId: string): Promise<any> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOYALTY_REWARD_BY_ID, "", {
          businessLocationId,
          rewardId,
        }),
        { method: "DELETE" },
      );
    },
    [fetchWithAuth],
  );

  const getClientLoyalty = useCallback(
    async (
      clientId: string,
      businessLocationId: number,
    ): Promise<ClientLoyaltyResponse | null> => {
      return fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.CLIENT_LOYALTY,
          `business_location_id=${encodeURIComponent(businessLocationId)}`,
          { clientId },
        ),
      );
    },
    [fetchWithAuth],
  );

  const adjustClientLoyaltyPoints = useCallback(
    async (
      clientId: string,
      data: AdjustClientLoyaltyPointsRequest,
    ): Promise<any> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_LOYALTY_ADJUST, "", { clientId }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const setClientLoyaltyOptIn = useCallback(
    async (
      clientId: string,
      data: SetClientLoyaltyOptInRequest,
    ): Promise<any> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_LOYALTY_OPT_IN, "", { clientId }),
        { method: "PUT", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const getTransactionAvailableLoyaltyRewards = useCallback(
    async (transactionId: string): Promise<AvailableLoyaltyReward[]> => {
      const response = await fetchWithAuth(
        getApiUrl(
          API_CONFIG.ENDPOINTS.TRANSACTION_LOYALTY_AVAILABLE_REWARDS,
          "",
          {
            transactionId,
          },
        ),
      );
      if (Array.isArray(response?.available_rewards))
        return response.available_rewards;
      if (Array.isArray(response)) return response;
      return [];
    },
    [fetchWithAuth],
  );

  const redeemLoyaltyReward = useCallback(
    async (
      transactionId: string,
      data: RedeemLoyaltyRewardRequest,
    ): Promise<RedeemLoyaltyRewardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTION_LOYALTY_REDEEM, "", {
          transactionId,
        }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  // Backend PR #499 — GET the points this transaction will earn once
  // settled. Not deployed yet; consumers must tolerate 404/5xx by hiding
  // the earned-points line silently.
  const getTransactionLoyaltyPreviewEarn = useCallback(
    async (
      transactionId: string,
      stagedRedemptionDollars?: number,
    ): Promise<LoyaltyEarnPreviewResponse | null> => {
      const base = getApiUrl(
        API_CONFIG.ENDPOINTS.TRANSACTION_LOYALTY_PREVIEW_EARN,
        "",
        { transactionId },
      );
      // Backend nets the staged (uncommitted) redemption off the earn
      // basis when this query param is present — same math as accrual, so
      // the FE gets the truthful post-redemption points without local
      // estimation. Absent / 0 falls through to the committed-only view.
      const url =
        stagedRedemptionDollars && stagedRedemptionDollars > 0
          ? `${base}${base.includes("?") ? "&" : "?"}staged_redemption_dollars=${encodeURIComponent(
              stagedRedemptionDollars,
            )}`
          : base;
      const response = await fetchWithAuth(url);
      if (response && typeof response === "object") return response;
      return null;
    },
    [fetchWithAuth],
  );

  // ==========================================
  // Gift cards (prepaid stored-value balances)
  // ==========================================
  const getGiftCardConfig = useCallback(
    async (salonId: string): Promise<GiftCardConfig> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GIFT_CARD_CONFIG, "", { salonId }),
      );
    },
    [fetchWithAuth],
  );

  const updateGiftCardConfig = useCallback(
    async (
      salonId: string,
      body: UpdateGiftCardConfigRequest,
    ): Promise<GiftCardConfig> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GIFT_CARD_CONFIG, "", { salonId }),
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const getClientGiftCards = useCallback(
    async (salonId: string, clientId: string): Promise<GiftCardsForClient> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GIFT_CARDS, "", {
          salonId,
          clientId,
        }),
      );
      // Tolerate a null/partial envelope so callers always get both arrays.
      const data = responseJson as Partial<GiftCardsForClient> | null;
      return {
        held: data?.held ?? [],
        purchased: data?.purchased ?? [],
      };
    },
    [fetchWithAuth],
  );

  const addClientGiftCard = useCallback(
    async (
      salonId: string,
      clientId: string,
      body: AddGiftCardRequest,
    ): Promise<AddGiftCardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GIFT_CARDS, "", {
          salonId,
          clientId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const adjustGiftCardBalance = useCallback(
    async (
      salonId: string,
      giftCardId: string,
      body: AdjustGiftCardRequest,
    ): Promise<AdjustGiftCardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.ADJUST_GIFT_CARD, "", {
          salonId,
          giftCardId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const voidGiftCard = useCallback(
    async (
      salonId: string,
      giftCardId: string,
      body: VoidGiftCardRequest,
    ): Promise<VoidGiftCardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.VOID_GIFT_CARD, "", {
          salonId,
          giftCardId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const voidTransaction = useCallback(
    async (
      businessLocationId: string,
      transactionId: string,
      body: VoidTransactionRequest = {},
    ): Promise<any> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.VOID_TRANSACTION, "", {
          businessLocationId,
          transactionId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  ) as AppContextType["voidTransaction"];

  const updateGiftCardCode = useCallback(
    async (
      salonId: string,
      giftCardId: string,
      body: UpdateGiftCardCodeRequest,
    ): Promise<UpdateGiftCardCodeResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.UPDATE_GIFT_CARD_CODE, "", {
          salonId,
          giftCardId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const getGiftCardLedger = useCallback(
    async (
      salonId: string,
      giftCardId: string,
    ): Promise<GiftCardLedgerEntry[]> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.GIFT_CARD_LEDGER, "", {
          salonId,
          giftCardId,
        }),
      );
      // The core GET endpoint returns a bare array; tolerate a { ledger: [] }
      // envelope too so either backend shape works.
      const data = responseJson as
        | GiftCardLedgerEntry[]
        | { ledger?: GiftCardLedgerEntry[] }
        | null;
      return Array.isArray(data) ? data : (data?.ledger ?? []);
    },
    [fetchWithAuth],
  );

  // --- Phase 2: staff-sold lifecycle (sell → notify/attach → redeem) --------
  // These call the core SENARY routes, registered only under /business-locations.
  // `businessLocationId` is the salon UUID (businessInfo.id) — same value the
  // /business-locations gift-card methods above pass as salonId.

  const sellGiftCard = useCallback(
    async (
      businessLocationId: string,
      body: SellGiftCardRequest,
    ): Promise<SellGiftCardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SELL_GIFT_CARD, "", {
          businessLocationId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const getTransactionGiftCards = useCallback(
    async (
      businessLocationId: string,
      transactionId: string,
    ): Promise<GiftCardSummary[]> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.TRANSACTION_GIFT_CARDS, "", {
          businessLocationId,
          transactionId,
        }),
      );
      const data = responseJson as Partial<TransactionGiftCardsResponse> | null;
      return data?.gift_cards ?? [];
    },
    [fetchWithAuth],
  );

  const listGiftCards = useCallback(
    async (businessLocationId: string): Promise<GiftCardListItem[]> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LIST_GIFT_CARDS, "", {
          businessLocationId,
        }),
      );
      const data = responseJson as Partial<ListGiftCardsResponse> | null;
      return data?.gift_cards ?? [];
    },
    [fetchWithAuth],
  );

  const lookupGiftCardByCode = useCallback(
    async (
      businessLocationId: string,
      code: string,
    ): Promise<GiftCardSummary> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.LOOKUP_GIFT_CARD_BY_CODE, "", {
          businessLocationId,
        }),
        {
          method: "POST",
          body: JSON.stringify({ code }),
        },
      );
      return (responseJson as LookupGiftCardByCodeResponse).gift_card;
    },
    [fetchWithAuth],
  );

  const attachGiftCard = useCallback(
    async (
      businessLocationId: string,
      giftCardId: string,
      body: AttachGiftCardRequest,
    ): Promise<AttachGiftCardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.ATTACH_GIFT_CARD, "", {
          businessLocationId,
          giftCardId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const sendGiftCardLink = useCallback(
    async (
      businessLocationId: string,
      giftCardId: string,
      body: SendGiftCardLinkRequest,
    ): Promise<SendGiftCardLinkResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.SEND_GIFT_CARD_LINK, "", {
          businessLocationId,
          giftCardId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  const redeemGiftCard = useCallback(
    async (
      businessLocationId: string,
      body: RedeemGiftCardRequest,
    ): Promise<RedeemGiftCardResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.REDEEM_GIFT_CARD, "", {
          businessLocationId,
        }),
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
    },
    [fetchWithAuth],
  );

  // --- Phase 3: staff-typed custom code + gift-card line item ---------------

  const checkGiftCardCodeAvailable = useCallback(
    async (businessLocationId: string, code: string): Promise<boolean> => {
      const responseJson = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CHECK_GIFT_CARD_CODE, "", {
          businessLocationId,
        }),
        {
          method: "POST",
          body: JSON.stringify({ code }),
        },
      );
      return (
        (responseJson as CheckGiftCardCodeResponse | null)?.available ?? false
      );
    },
    [fetchWithAuth],
  );

  // Plain const (not useCallback) to mirror addLineItemToTransaction, which it
  // composes; the exported facade stabilizes its identity regardless.
  const addGiftCardLineItem = async (
    transactionId: string,
    giftCard: GiftCardLineItemInput,
  ) => {
    return addLineItemToTransaction(transactionId, {
      gift_cards: [giftCard],
    });
  };

  // === Identity-stable public surface (multi-location loop fix, 2026-07-23) =
  // Every method above (except fetchWithAuth) is a per-render closure — 85
  // plain async fns and 49 useCallbacks, many of which used to re-mint on
  // every render. Handing those identities out put them in the dependency
  // chain of ~every data-fetch effect app-wide, so ONE AppProvider re-render
  // (any token-state change) refired EVERY mounted fetch effect — the
  // full-app request bursts captured live. `methodsImpl` collects the fresh
  // closures each render; the exported `value` is a facade built ONCE whose
  // entries delegate to the freshest closure via methodsImplRef. Identities
  // never change; behavior always uses current state.
  // Typed as AppContextType so a member declared on the type but missing here
  // (or vice versa) is a COMPILE error, not a runtime "undefined is not a
  // function" at the first consumer call — the facade below is built from
  // Object.keys(methodsImpl), so this literal is the single source of truth.
  const methodsImpl: AppContextType = {
    getIdToken,
    fetchWithAuth,
    getSalons,
    getSalonByUuid,
    getServices,
    createServices,
    updateServices,
    bulkUpdateBusinessCharges,
    deleteSalonService,
    deleteStaffService,
    getSalonPolicies,
    getPolicyDefaults,
    createPolicy,
    updatePolicy,
    deletePolicy,
    getTransactions,
    getSales,
    getTransaction,
    getSuggestedTimes,
    getClient,
    addLineItemToTransaction,
    createAppointment,
    updateAppointment,
    createTimeBlock,
    updateTimeBlock,
    getTimeBlocks,
    deleteTimeBlock,
    createSalon,
    updateSalon,
    getSalonStaff,
    updateStaff,
    deleteStaff,
    inviteStaff,
    createStaffHoursOverride,
    deleteStaffHoursOverride,
    getStaffHoursOverrides,
    getStaffHoursOverrideEffective,
    getSalonHoursOverrides,
    getCheckoutUrl,
    getSubscriptionPricing,
    createStripeConnectAccount,
    getStripeConnectAccountLink,
    initializeStripeConnectAccountSession,
    registerStripeConnectTerminalDevice,
    getStripeConnectTerminalDevice,
    removeStripeTerminalDevice,
    getPaymentTerminals,
    updatePaymentTerminalLabel,
    setTerminalConfiguration,
    createPaymentIntent,
    updatePaymentIntent,
    cancelPaymentIntent,
    processPayment,
    simulatePayment,
    pollStripePaymentStatus,
    createQuickSale,
    getNotifications,
    updateNotificationReadStatus,
    getNotificationPreferences,
    updateNotificationPreferences,
    getDepositConfigs,
    createDepositConfig,
    updateDepositConfig,
    deleteDepositConfig,
    getSalonPaymentMethods,
    addSalonPaymentMethod,
    deleteSalonPaymentMethod,
    setSalonPaymentMethodsSelection,
    getDiscountPresets,
    createDiscountPreset,
    updateDiscountPreset,
    deleteDiscountPreset,
    setQuickDiscounts,
    getTaxRates,
    createTaxRate,
    updateTaxRate,
    deleteTaxRate,
    setProductTaxRates,
    setSalonServiceTaxRates,
    editTransaction,
    editTransactionLineItems,
    processNonCardPayment,
    getCashDrawerOverview,
    getCashDrawerSession,
    countCashDrawer,
    recordCashAdjustment,
    voidCashTransaction,
    updateCashDrawerBalancingDate,
    getCashDrawerBalancingSummary,
    uploadImage,
    removeSalonLogo,
    getImages,
    deleteImage,
    listSalonDocuments,
    uploadSalonDocument,
    getSalonDocumentDetail,
    deleteSalonDocument,
    revertSalonDocument,
    downloadSalonDocument,
    getRollfiPayrollPeriod,
    createRollfiBusiness,
    getRollfiActiveUsers,
    syncRollfiPayroll,
    initiateSso,
    prepPayrollSync,
    getTimeTrackingStatus,
    performTimeTrackingAction,
    getPayPeriodTimeCards,
    getEmployeePayPeriodTimeCard,
    updateTimeCardApprovals,
    deleteTimeEntry,
    upsertTimeEntry,
    syncTimeCardsToPayroll,
    getTimeTrackingPolicy,
    addTimeTrackingPolicy,
    updateTimeTrackingPolicy,
    deleteTimeTrackingPolicy,
    addPolicyAssignments,
    removePolicyAssignments,
    // Onboarding
    getOnboardingProgress,
    updateOnboardingProgress,
    getOnboardingQuestions,
    upsertOnboardingAnswer,
    // Product stock adjustments
    createProductStockAdjustment,
    updateProductStockAdjustment,
    deleteProductStockAdjustment,
    // Availability
    getAvailability,
    // Commissions
    getStaffCommissions,
    createCommissions,
    deleteCommission,
    getStaffTiers,
    createStaffTier,
    updateStaffTier,
    reorderStaffTiers,
    reorderStaff,
    deleteStaffTier,
    assignStaffTier,
    getTierCommissionDefault,
    upsertTierCommissionDefault,
    deleteTierCommissionDefault,
    applyTierCommissionDefaults,
    getTierServiceDefaults,
    upsertTierServiceDefaults,
    deleteTierServiceDefault,
    applyTierServiceDefaults,
    reassignAndDeleteTier,
    // Card-on-File
    createSetupIntent,
    getClientPaymentMethods,
    deleteClientPaymentMethod,
    setDefaultClientPaymentMethod,
    setClientPaymentMethodNickname,
    chargeCardOnFile,
    refundPayment,
    sendReceipt,
    // Service Packages (prepaid bundles)
    getServicePackages,
    createServicePackage,
    updateServicePackage,
    deleteServicePackage,
    getClientServicePackages,
    getClientActivity,
    getClientPackageAdjustments,
    addClientServicePackage,
    adjustClientPackageBalance,
    editClientPackageExpiry,
    revokeClientPackage,
    sellServicePackage,
    redeemPackage,
    // Gift cards (prepaid stored-value balances)
    getGiftCardConfig,
    updateGiftCardConfig,
    getClientGiftCards,
    addClientGiftCard,
    adjustGiftCardBalance,
    voidGiftCard,
    voidTransaction,
    updateGiftCardCode,
    getGiftCardLedger,
    listGiftCards,
    // Gift cards Phase 2 — staff-sold lifecycle
    sellGiftCard,
    getTransactionGiftCards,
    lookupGiftCardByCode,
    attachGiftCard,
    sendGiftCardLink,
    redeemGiftCard,
    // Gift cards Phase 3 — custom code + line item
    checkGiftCardCodeAvailable,
    addGiftCardLineItem,
    // Feature access
    getAccess,
    patchStaffUsersMe,
    // Role permissions (RBAC) - workflow-based
    getRolePermissions,
    batchUpdateRolePermissions,
    resetRolePermissions,
    // Loyalty
    getLoyaltySettings,
    getLoyaltyLocationSummary,
    updateLoyaltySettings,
    getLoyaltyEarnRules,
    createLoyaltyEarnRule,
    updateLoyaltyEarnRule,
    deleteLoyaltyEarnRule,
    getLoyaltyRewards,
    createLoyaltyReward,
    updateLoyaltyReward,
    deleteLoyaltyReward,
    getClientLoyalty,
    adjustClientLoyaltyPoints,
    setClientLoyaltyOptIn,
    getTransactionAvailableLoyaltyRewards,
    redeemLoyaltyReward,
    getTransactionLoyaltyPreviewEarn,
  };

  const methodsImplRef = useRef(methodsImpl);
  methodsImplRef.current = methodsImpl;
  // Built exactly once (useState initializer); every entry has a permanent
  // identity. The key set is static, so first-render keys cover all methods.
  const [value] = useState<AppContextType>(() => {
    const facade: Record<string, (...args: unknown[]) => unknown> = {};
    for (const key of Object.keys(methodsImpl)) {
      facade[key] = (...args: unknown[]) =>
        (
          methodsImplRef.current as unknown as Record<
            string,
            (...a: unknown[]) => unknown
          >
        )[key](...args);
    }
    return facade as unknown as AppContextType;
  });

  // [LOOP] probe: post-fix these identities must NEVER change after the first
  // render — if this logs more than once, the stable facade regressed.
  useValueChurnProbe("AppContext.value", {
    value,
    getOnboardingProgress: value.getOnboardingProgress,
    getSalons: value.getSalons,
    getServices: value.getServices,
  });

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
